<template>
  <div>
    <div v-if="isPageMode" class="send-report-page body-font"
    :class="{ 'wizard-step-one': isWizardMode && currentStep === 1 }">
    <div v-if="showHeader" class="page-header">
      <div>
        <div class="page-step-label" v-if="isWizardMode">{{ $t('sendReport.stepLabel', {
          current: currentStep, total: 3
        }) }}</div>
        <h4 class="page-title">{{ $t('sendReport.pageTitle') }}</h4>
        <p class="page-subtitle">{{ $t('sendReport.newRequestHint') }}</p>
      </div>
      <button v-if="isAdminStandard" type="button" class="page-close-btn admin-close-btn" @click="closeModal">
        <CIcon name="cil-x" class="mr-1" /> {{ $t('common.close') }}
      </button>
      <button v-else type="button" class="page-close-btn keyboard-action" :aria-label="$t('common.close')" @click="closeModal">✕</button>
    </div>

    <div v-if="isWizardMode" class="wizard-progress-block">
      <div class="wizard-progress-heading">
        <h3>{{ currentStepTitle }}</h3>
      </div>
      <div class="step-progress" role="progressbar" :aria-valuenow="currentStep" aria-valuemin="1" aria-valuemax="3">
        <div v-for="step in 3" :key="step" class="step-indicator" :class="{ active: currentStep >= step }">
          <span>{{ step }}</span>
        </div>
      </div>
    </div>

    <CForm class="page-form" @submit.prevent>
      <!-- Rating Gatekeeping Lock Alert -->
      <CAlert v-if="ratingLockMessage" color="danger" class="d-flex align-items-center mb-4">
        <CIcon name="cil-star" class="mr-2 flex-shrink-0" />
        <div class="d-flex justify-content-between align-items-center w-100 flex-wrap">
          <span class="font-weight-bold">{{ ratingLockMessage }}</span>
          <CButton color="primary" size="sm" class="ml-auto my-1" @click="handleGoToRating">
            <CIcon name="cil-star" class="mr-1" /> {{ $t('sendReport.goToRating') || 'ไปที่แบบประเมิน' }}
          </CButton>
        </div>
      </CAlert>
      <!-- Affiliation status warning alert -->
      <CAlert v-if="affiliationStatusMessage" color="warning" class="d-flex align-items-center mb-4">
        <CIcon name="cil-warning" class="mr-2 flex-shrink-0" />
        <span>{{ affiliationStatusMessage }}</span>
      </CAlert>
      <div v-show="!isWizardMode || currentStep === 1">
        <div class="d-flex align-items-center mb-4">
          <div class="icon-box bg-gradient-warning mr-3">
            <span class="text-white">
              <CIcon name="cil-settings" size="xl" />
            </span>
          </div>
          <div>
            <h5 class="m-0 font-weight-bold">{{ $t('sendReport.sectionRepair') }}</h5>
            <small class="text-muted">{{ $t('sendReport.sectionRepairHint') }}</small>
          </div>
        </div>

        <div class="mb-3">
          <label class="lbl">{{ $t('sendReport.jobType') }} <span class="text-danger">*</span></label>

          <div>
            <div class="category-selection-panel">
              <!-- Categories Section -->

              <!-- 2. All Categories Section -->
              <div class="category-section all-categories-section">
                <div class="category-section-header">
                  <span class="category-section-title">
                    <CIcon name="cil-grid" class="section-grid-icon" />
                    {{ $t('sendReport.allCategories') || 'All Categories' }}
                  </span>
                  <div class="category-search-wrapper">
                    <CIcon name="cil-search" class="search-icon" />
                    <input
                      type="text"
                      v-model="categorySearchQuery"
                      class="category-search-input"
                      :placeholder="$t('sendReport.searchCategoryPlaceholder') || 'Search categories...'"
                      aria-label="Search categories" />
                    <button
                      v-if="categorySearchQuery"
                      type="button"
                      class="search-clear-btn"
                      @click="categorySearchQuery = ''"
                      aria-label="Clear search">✕</button>
                  </div>
                </div>

                <div v-if="filteredCategories.length > 0" class="category-icon-grid" role="radiogroup" :aria-label="$t('sendReport.allCategories') || 'All Categories'">
                  <button
                    type="button"
                    v-for="item in filteredCategories"
                    :key="`all-${item.canonicalKey}`"
                    class="category-icon-btn"
                    :class="{ active: isCategoryActive(item) }"
                    role="radio"
                    :aria-checked="String(isCategoryActive(item))"
                    :aria-label="item.label"
                    :title="item.label"
                    @click="selectCategory(item)">
                    <img
                      v-if="isCustomImageIcon(item) && !brokenCustomIcons[item.canonicalKey || item.value]"
                      :src="getCustomImageUrl(item.icon)"
                      :alt="item.label"
                      class="category-custom-img-icon"
                      @error="handleIconImgError(item)"
                    />
                    <CIcon
                      v-else
                      :name="getCategoryIcon(item)"
                      size="xl"
                      class="category-svg-icon"
                    />
                    <span class="category-tooltip" role="tooltip" aria-hidden="true">{{ item.label }}</span>
                  </button>
                </div>
                <div v-else class="category-empty-search text-muted">
                  {{ $t('sendReport.noCategoriesFound') || 'No matching categories found' }}
                </div>
              </div>

              <!-- 3. Selected Category Feedback -->
              <transition name="fade">
                <div v-if="formData.selectedSubCategory" class="selected-category-bar">
                  <div class="d-flex align-items-center flex-grow-1 min-w-0">
                    <span class="selected-label-text">
                      <strong class="text-primary mr-1">{{ $t('sendReport.jobType') }}:</strong>
                      <span class="text-dark font-weight-bold">{{ selectedCategoryDisplayLabel }}</span>
                    </span>
                  </div>
                  <button
                    type="button"
                    class="btn btn-sm btn-link text-danger p-0 ml-3 flex-shrink-0 font-weight-bold"
                    :aria-label="$t('common.cancel') || 'Cancel'"
                    @click="formData.selectedSubCategory = null">
                    ✕
                  </button>
                </div>
              </transition>
            </div>

            <div v-if="errors.subCategories" class="text-danger mt-2">
              {{ errors.subCategories }}
            </div>

            <transition name="fade">
              <div v-if="formData.selectedSubCategory && formData.selectedSubCategory.value === 'other'" class="mt-3">
                <label class="lbl">{{ $t('sendReport.otherTypeLabel') }} <span class="text-danger">*</span></label>
                <CInput v-model="formData.otherCategoryText" :placeholder="$t('sendReport.otherTypePlaceholder')"
                  :class="{ 'is-invalid': errors.otherCategoryText }" />
                <div v-if="errors.otherCategoryText" class="invalid-feedback d-block">
                  {{ errors.otherCategoryText }}
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>

      <div v-show="!isWizardMode || currentStep === 2">
        <transition name="fade">
          <div v-if="isEquipmentRelated" class="my-3">
            <label class="lbl">
              {{ $t('sendReport.assetCodeLabel') }} <span class="text-danger">*</span>
            </label>
            <CInput
              v-model="formData.assetCode"
              :placeholder="$t('sendReport.assetCodePlaceholder')"
              :class="{ 'is-invalid': errors.assetCode }"
            />
            <div v-if="errors.assetCode" class="invalid-feedback d-block">
              {{ errors.assetCode }}
            </div>
            <small class="text-muted mt-1 d-block">
              {{ $t('sendReport.assetCodeHint') }}
            </small>
          </div>
        </transition>

        <div class="my-3">
          <label class="lbl">{{ $t('sendReport.problemLabel') }} <span class="text-danger">*</span></label>
          <CTextarea v-model="formData.problemDetails" :placeholder="$t('sendReport.problemPlaceholder')" rows="3"
            :class="{ 'is-invalid': errors.problemDetails }" />
          <div v-if="errors.problemDetails" class="invalid-feedback d-block">
            {{ errors.problemDetails }}
          </div>
        </div>

        <div class="my-4">
          <label class="lbl">{{ $t('sendReport.imageLabel') }} <span class="text-danger">*</span></label>
          <ImageAttachment class="custom-file-upload-wrapper" :preview="repairImagePreview || ''" :filename="repairImage ? repairImage.name : ''" :max-count="3"
              :invalid="Boolean(errors.problemImage)" @change="handleFileUpload" @remove="removeImage" />
          <small class="text-muted mt-2 d-block">{{ $t('sendReport.imageHint') }}</small>
          <div v-if="errors.problemImage" class="text-danger mt-2">
            {{ errors.problemImage }}
          </div>
        </div>
      </div>

      <div v-show="!isWizardMode || currentStep === 3">
        <div class="mb-3">
          <label class="lbl">{{ $t('sendReport.workArea') }} <span class="text-danger">*</span></label>
          <div class="area-grid">
            <label class="area-card" :class="{ active: formData.locationType === 'building' }">
              <input type="radio" value="building" v-model="formData.locationType" class="sr-only" :name="'request-location-page-' + _uid" />
              <div class="mb-1 area-card-icon">
                <CIcon name="cil-building" size="xl" />
              </div>
              <div>
                <strong>{{ $t('sendReport.insideBuilding') }}</strong><br />
                <small class="text-muted">{{ $t('sendReport.insideBuildingSub') }}</small>
              </div>
              <div class="check">✓</div>
            </label>
            <label class="area-card" :class="{ active: formData.locationType === 'outdoor' }">
              <input type="radio" value="outdoor" v-model="formData.locationType" class="sr-only" :name="'request-location-page-' + _uid" />
              <div class="mb-1 area-card-icon">
                <CIcon name="cil-location-pin" size="xl" />
              </div>
              <div>
                <strong>{{ $t('sendReport.outsideBuilding') }}</strong><br />
                <small class="text-muted">{{ $t('sendReport.outsideBuildingSub') }}</small>
              </div>
              <div class="check">✓</div>
            </label>
          </div>
          <div v-if="errors.locationType" class="text-danger mt-2">
            {{ errors.locationType }}
          </div>
        </div>

        <transition name="fade" mode="out-in">
          <div v-if="formData.locationType === 'building'" class="sub-box">
            <div class="d-flex align-items-start">
              <div class="flex-grow-1" style="min-width: 0;">
                <label class="lbl">{{ $t('sendReport.buildingRoom') }} <span class="text-danger">*</span></label>
                <div class="position-relative">
                  <input type="text" ref="buildingInput" class="form-control" v-model="buildingSearchDetails"
                    :placeholder="$t('sendReport.buildingPlaceholder')" @input="buildingActiveIndex = -1; onBuildingInputChange()"
                    @focus="showBuildingSuggestions = true; buildingActiveIndex = -1" @blur="showBuildingSuggestions = false; buildingActiveIndex = -1"
                    @keydown="onBuildingKeydown" v-listbox-escape="closeBuildingSuggestions" role="combobox" :aria-controls="'building-page-' + _uid" :aria-activedescendant="buildingSuggestionsVisible && buildingActiveIndex >= 0 ? 'building-page-' + _uid + '-' + buildingActiveIndex : null" aria-autocomplete="list" :aria-expanded="String(Boolean(buildingSuggestionsVisible))" :aria-label="$t('sendReport.buildingRoom')"
                    :class="{ 'is-invalid': errors.buildingId }" autocomplete="off">
                  <ul
                    v-if="buildingSuggestionsVisible"
                    :id="'building-page-' + _uid" role="listbox" :aria-label="$t('sendReport.buildingRoom')"
                    class="list-group position-absolute w-100 shadow bg-white"
                    style="z-index: 1050; max-height: 250px; overflow-y: auto; background-color: white;">
                    <li v-for="(b, index) in buildingSuggestions" :key="b._id"
                      class="list-group-item list-group-item-action cursor-pointer py-2 bg-white keyboard-listbox-option"
                      :id="'building-page-' + _uid + '-' + index" role="option" :aria-selected="String(buildingActiveIndex === index)" :data-keyboard-active="buildingActiveIndex === index"
                      @mousedown.prevent="selectBuildingSuggestion(b)" style="cursor: pointer;">
                      <i class="cil-building mr-2 text-primary"></i>
                      <span><strong>{{ getBuildingDisplayName(b) }}</strong></span>
                    </li>
                  </ul>
                  <div
                    v-if="requiresRoom && buildingSearchDetails && (!buildingSearchDetails.includes('-') || buildingSearchDetails.split('-').filter(Boolean).length < 2)"
                    class="text-danger small mt-1 ml-1">
                    {{ $t('sendReport.buildingFormatHint') }}
                  </div>
                  <div v-if="selectedBuilding" class="text-success small mt-1 ml-1">
                    <i class="cil-check-circle"></i> {{ $t('sendReport.buildingFound') }} {{
                      getBuildingDisplayName(selectedBuilding)
                    }}
                  </div>
                  <CAlert v-if="isPropertyLocation" color="info" class="d-flex align-items-center mt-2 mb-0 py-2">
                    <CIcon name="cil-info" class="mr-2 flex-shrink-0" />
                    <small>{{ $t('sendReport.propertySectionNotice') }}</small>
                  </CAlert>
                </div>
                <div v-if="errors.buildingId" class="invalid-feedback d-block">
                  {{ errors.buildingId }}
                </div>
              </div>
            </div>
          </div>
          <div v-else-if="formData.locationType === 'outdoor'" class="sub-box">
            <label class="lbl">{{ $t('sendReport.pinLocation') }} <span class="text-danger">*</span></label>
            <div class="border rounded overflow-hidden">
              <GoogleMapsPicker v-model="outdoorLocation" @input="onLocationChange" />
            </div>
            <div v-if="errors.location" class="text-danger mt-2">
              {{ errors.location }}
            </div>
          </div>
        </transition>

        <hr class="my-4 border-dashed" />

        <div class="d-flex align-items-center mb-4">
          <div class="icon-box bg-gradient-primary mr-3">
            <span class="text-white">
              <CIcon name="cil-user" size="xl" />
            </span>
          </div>
          <div>
            <h5 class="m-0 font-weight-bold">{{ $t('sendReport.reporterSection') }}</h5>
            <small class="text-muted">{{ $t('sendReport.reporterHint') }}</small>
          </div>
        </div>

        <div class="reporter-details-grid mb-3">
          <div class="reporter-field">
            <label class="lbl">{{ $t('sendReport.reporterName') }}</label>
            <div id="reporterNameInput" class="reporter-name-text">
              {{ currentUser.name }} {{ currentUser.surname }}
            </div>
          </div>
          <div class="reporter-field">
            <label class="lbl">{{ $t('sendReport.affiliation') }}</label>
            <div id="affiliationInput" class="reporter-name-text">
              {{ resolvedAffiliationName }}
            </div>
            <div v-if="affiliationStatusMessage" class="text-danger small mt-1 font-weight-bold">
              <CIcon name="cil-warning" class="mr-1" />
              {{ affiliationStatusMessage }}
            </div>
          </div>
          <div class="reporter-field">
            <label class="lbl" for="personalPhoneInput">
              {{ $t('sendReport.personalPhone') }} <span class="text-danger">*</span>
            </label>
            <CInput id="personalPhoneInput" name="personalPhone" v-model="formData.personalPhone"
              :placeholder="$t('sendReport.phonePlaceholderMobile')" maxlength="10"
              @input="formData.personalPhone = formData.personalPhone.replace(/[^0-9]/g, '')"
              :class="{ 'is-invalid': errors.personalPhone }" />
            <div v-if="errors.personalPhone" class="invalid-feedback d-block">
              {{ errors.personalPhone }}
            </div>
          </div>
          <div class="reporter-field">
            <label class="lbl" for="onBehalfOfInput">{{ $t('sendReport.onBehalfLabel') }}</label>
            <CInput id="onBehalfOfInput" name="onBehalfOf" v-model="formData.onBehalfOf"
              :placeholder="$t('sendReport.onBehalfPlaceholder')" autocomplete="off" />
          </div>
          <div class="reporter-field">
            <label class="lbl" for="telephoneWorkInput">
              {{ $t('sendReport.workPhone') }}
            </label>
            <CInput id="telephoneWorkInput" name="telephoneWork" v-model="formData.telephoneWork"
              :placeholder="$t('sendReport.phonePlaceholderWork')" maxlength="10"
              @input="formData.telephoneWork = formData.telephoneWork.replace(/[^0-9]/g, '')"
              :class="{ 'is-invalid': errors.telephoneWork }" />
            <div v-if="errors.telephoneWork" class="invalid-feedback d-block">
              {{ errors.telephoneWork }}
            </div>
          </div>
          <div class="reporter-field reporter-field--schedule">
            <label class="lbl">{{ $t('sendReport.availability') }} <span v-if="formData.locationType !== 'outdoor'"
                class="text-danger">*</span></label>
            <div class="date-time-container">
              <div class="mb-2">
                <label class="time-label mb-1" for="datePickerInput">{{ $t('sendReport.dateLabel') }}</label>
                <div
                  class="date-display-wrapper"
                  :class="{ 'is-invalid': errors.availableTime }"
                  @click="openDatePicker('datePickerMobile')"
                >
                  <input
                    type="text"
                    class="date-display-input"
                    :value="displaySelectedDate"
                    :placeholder="datePlaceholder"
                    readonly
                    @keydown.enter.prevent="openDatePicker('datePickerMobile')"
                    @keydown.space.prevent="openDatePicker('datePickerMobile')"
                  />
                  <span class="date-picker-icon" :title="$t('sendReport.pickDate') || 'เลือกวันที่'">
                    <CIcon name="cil-calendar" />
                  </span>
                  <input
                    ref="datePickerMobile"
                    id="datePickerInput"
                    name="selectedDate"
                    type="date"
                    v-model="formData.selectedDate"
                    :min="minDate"
                    class="date-native-hidden"
                    tabindex="-1"
                    aria-hidden="true"
                    @change="updateAvailableTime"
                  />
                </div>
              </div>
              <div class="time-range-container">
                <!-- เวลาเริ่มต้น -->
                <div class="time-input-group">
                  <label class="time-label" for="startTimeInput">
                    {{ $t('sendReport.startLabel') }}
                  </label>

                  <div class="time-picker-wrapper" @click="triggerTimePicker('startTime')">
                    <span class="time-picker-icon" :title="$t('sendReport.pickTime') || 'เลือกเวลา'">
                      <CIcon name="cil-clock" />
                    </span>
                    <input id="startTimeInput" name="startTime" type="text" readonly role="button" aria-haspopup="dialog" :aria-expanded="String(showTimePickerModal && activeTimePickerField === 'startTime')" @keydown.enter.prevent="triggerTimePicker('startTime')" @keydown.space.prevent="triggerTimePicker('startTime')"
                      :value="formData.startTime || '--:--'" class="form-control time-input text-center"
                      :class="{ 'is-invalid': errors.availableTime }" />
                  </div>
                </div>

                <div class="time-separator">-</div>

                <!-- เวลาสิ้นสุด -->
                <div class="time-input-group">
                  <label class="time-label" for="endTimeInput">
                    {{ $t('sendReport.endLabel') }}
                  </label>

                  <div class="time-picker-wrapper" :class="{ 'disabled': !formData.startTime }" @click="triggerTimePicker('endTime')">
                    <span class="time-picker-icon" :title="$t('sendReport.pickTime') || 'เลือกเวลา'">
                      <CIcon name="cil-clock" />
                    </span>
                    <input id="endTimeInput" name="endTime" type="text" readonly role="button" aria-haspopup="dialog" :aria-expanded="String(showTimePickerModal && activeTimePickerField === 'endTime')" @keydown.enter.prevent="triggerTimePicker('endTime')" @keydown.space.prevent="triggerTimePicker('endTime')"
                      :value="formData.endTime || '--:--'" class="form-control time-input text-center"
                      :class="{ 'is-invalid': errors.availableTime }" :disabled="!formData.startTime" />
                  </div>
                </div>
              </div>
            </div>
            <small class="text-muted">{{ $t('sendReport.availabilityHint') }}</small>
            <div v-if="errors.availableTime" class="invalid-feedback d-block">
              {{ errors.availableTime }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="isWizardMode" class="page-actions">
        <CButton v-if="currentStep > 1" color="danger" variant="outline"
          class="wizard-back-btn request-cancel-btn flex-grow-1 font-weight-bold" :class="{ 'admin-action-btn': isAdminStandard }" @click="handleMobileBack" :disabled="isSubmitting">
          <CIcon v-if="isAdminStandard" name="cil-arrow-thick-left" class="mr-1" />
          {{ currentStep === 1 ? $t('sendReport.cancel') : $t('sendReport.back') }}
        </CButton>
        <CButton color="success" class="wizard-next-btn request-confirm-btn flex-grow-1 font-weight-bold text-white"
          :class="{ 'admin-action-btn': isAdminStandard }"
          @click="handleMobileNext" :disabled="isSubmitting || (currentStep >= 3 && isSubmissionBlocked)">
          <span v-if="currentStep < 3">
            {{ $t('sendReport.nextStep') }}
          </span>
          <span v-else>
            {{ $t('sendReport.submitRequest') }}
          </span>
        </CButton>
      </div>

      <div v-else-if="isPageMode && !isWizardMode" class="page-actions page-actions-desktop">
        <CButton color="secondary" variant="outline" class="request-cancel-btn flex-grow-1 mr-2 font-weight-bold"
          :class="{ 'admin-action-btn': isAdminStandard }" @click="closeModal"
          :disabled="isSubmitting">
          {{ $t('sendReport.cancel') }}
        </CButton>
        <CButton color="success" class="request-confirm-btn flex-grow-1 font-weight-bold text-white" @click="sendReport"
          :disabled="isSubmitting || isSubmissionBlocked">
          <span v-if="!isSubmitting">
            {{ $t('sendReport.confirm') }}
          </span>
          <span v-else>
            <span class="spinner-border spinner-border-sm mr-1"></span> {{ $t('sendReport.submitting') }}
          </span>
        </CButton>
      </div>
    </CForm>
  </div>

  <CModal v-else size="lg" :show.sync="localModal" :close-on-backdrop="false" :close-button="!isAdminStandard">
    <template v-if="isAdminStandard" #header>
      <h5 class="modal-title mb-0 font-weight-bold">{{ $t('sendReport.modalTitle') }}</h5>
      <CButton size="sm" color="secondary" variant="outline" class="ml-auto admin-close-btn" @click="closeModal">
        <CIcon name="cil-x" class="mr-1" /> {{ $t('common.close') }}
      </CButton>
    </template>
    <CModalBody class="p-4 body-font">
      <CForm @submit.prevent>
        <!-- Rating Gatekeeping Lock Alert -->
        <CAlert v-if="ratingLockMessage" color="danger" class="d-flex align-items-center mb-4">
          <CIcon name="cil-star" class="mr-2 flex-shrink-0" />
          <div class="d-flex justify-content-between align-items-center w-100 flex-wrap">
            <span class="font-weight-bold">{{ ratingLockMessage }}</span>
            <CButton color="primary" size="sm" class="ml-auto my-1" @click="handleGoToRating">
              <CIcon name="cil-star" class="mr-1" /> {{ $t('sendReport.goToRating') || 'ไปที่แบบประเมิน' }}
            </CButton>
          </div>
        </CAlert>
        <!-- Affiliation status warning alert -->
        <CAlert v-if="affiliationStatusMessage" color="warning" class="d-flex align-items-center mb-4">
          <CIcon name="cil-warning" class="mr-2 flex-shrink-0" />
          <span>{{ affiliationStatusMessage }}</span>
        </CAlert>
        <div>
          <div class="d-flex align-items-center mb-4">
            <div class="icon-box bg-gradient-warning mr-3">
              <span class="text-white">
                <CIcon name="cil-settings" size="xl" />
              </span>
            </div>
            <div>
              <h5 class="m-0 font-weight-bold">{{ $t('sendReport.sectionRepair') }}</h5>
              <small class="text-muted">{{ $t('sendReport.sectionRepairHint') }}</small>
            </div>
          </div>

          <div class="mb-3">
            <label class="lbl">{{ $t('sendReport.jobType') }} <span class="text-danger">*</span></label>

            <!-- Predefined categories multiselect (Single Select) -->
            <div>

            <div class="category-selection-panel">
              <!-- Categories Section -->

              <!-- 2. All Categories Section -->
              <div class="category-section all-categories-section">
                <div class="category-section-header">
                  <span class="category-section-title">
                    <CIcon name="cil-grid" class="section-grid-icon" />
                    {{ $t('sendReport.allCategories') || 'All Categories' }}
                  </span>
                  <div class="category-search-wrapper">
                    <CIcon name="cil-search" class="search-icon" />
                    <input
                      type="text"
                      v-model="categorySearchQuery"
                      class="category-search-input"
                      :placeholder="$t('sendReport.searchCategoryPlaceholder') || 'Search categories...'"
                      aria-label="Search categories" />
                    <button
                      v-if="categorySearchQuery"
                      type="button"
                      class="search-clear-btn"
                      @click="categorySearchQuery = ''"
                      aria-label="Clear search">✕</button>
                  </div>
                </div>

                <div v-if="filteredCategories.length > 0" class="category-icon-grid" role="radiogroup" :aria-label="$t('sendReport.allCategories') || 'All Categories'">
                  <button
                    type="button"
                    v-for="item in filteredCategories"
                    :key="`all-${item.canonicalKey}`"
                    class="category-icon-btn"
                    :class="{ active: isCategoryActive(item) }"
                    role="radio"
                    :aria-checked="String(isCategoryActive(item))"
                    :aria-label="item.label"
                    :title="item.label"
                    @click="selectCategory(item)">
                    <img
                      v-if="isCustomImageIcon(item) && !brokenCustomIcons[item.canonicalKey || item.value]"
                      :src="getCustomImageUrl(item.icon)"
                      :alt="item.label"
                      class="category-custom-img-icon"
                      @error="handleIconImgError(item)"
                    />
                    <CIcon
                      v-else
                      :name="getCategoryIcon(item)"
                      size="xl"
                      class="category-svg-icon"
                    />
                    <span class="category-tooltip" role="tooltip" aria-hidden="true">{{ item.label }}</span>
                  </button>
                </div>
                <div v-else class="category-empty-search text-muted">
                  {{ $t('sendReport.noCategoriesFound') || 'No matching categories found' }}
                </div>
              </div>

              <!-- 3. Selected Category Feedback -->
              <transition name="fade">
                <div v-if="formData.selectedSubCategory" class="selected-category-bar">
                  <div class="d-flex align-items-center flex-grow-1 min-w-0">
                    <span class="selected-label-text">
                      <strong class="text-primary mr-1">{{ $t('sendReport.jobType') }}:</strong>
                      <span class="text-dark font-weight-bold">{{ selectedCategoryDisplayLabel }}</span>
                    </span>
                  </div>
                  <button
                    type="button"
                    class="btn btn-sm btn-link text-danger p-0 ml-3 flex-shrink-0 font-weight-bold"
                    :aria-label="$t('common.cancel') || 'Cancel'"
                    @click="formData.selectedSubCategory = null">
                    ✕
                  </button>
                </div>
              </transition>
            </div>

              <div v-if="errors.subCategories" class="text-danger mt-2">
                {{ errors.subCategories }}
              </div>

              <!-- Other category input -->
              <transition name="fade">
                <div v-if="formData.selectedSubCategory && formData.selectedSubCategory.value === 'other'" class="mt-3">
                  <label class="lbl">{{ $t('sendReport.otherTypeLabel') }} <span class="text-danger">*</span></label>
                  <CInput v-model="formData.otherCategoryText" :placeholder="$t('sendReport.otherTypePlaceholder')"
                    :class="{ 'is-invalid': errors.otherCategoryText }" />
                  <div v-if="errors.otherCategoryText" class="invalid-feedback d-block">
                    {{ errors.otherCategoryText }}
                  </div>
                </div>
              </transition>
            </div>
          </div>

          <transition name="fade">
            <div v-if="isEquipmentRelated" class="my-3">
              <label class="lbl">
                {{ $t('sendReport.assetCodeLabel') }} <span class="text-danger">*</span>
              </label>
              <CInput
                v-model="formData.assetCode"
                :placeholder="$t('sendReport.assetCodePlaceholder')"
                :class="{ 'is-invalid': errors.assetCode }"
              />
              <div v-if="errors.assetCode" class="invalid-feedback d-block">
                {{ errors.assetCode }}
              </div>
              <small class="text-muted mt-1 d-block">
                {{ $t('sendReport.assetCodeHint') }}
              </small>
            </div>
          </transition>

          <div class="my-3">
            <label class="lbl">{{ $t('sendReport.problemLabel') }} <span class="text-danger">*</span></label>
            <CTextarea v-model="formData.problemDetails" :placeholder="$t('sendReport.problemPlaceholder')" rows="3"
              :class="{ 'is-invalid': errors.problemDetails }" />
            <div v-if="errors.problemDetails" class="invalid-feedback d-block">
              {{ errors.problemDetails }}
            </div>
          </div>

          <div class="my-4">
            <label class="lbl">{{ $t('sendReport.imageLabel') }} <span class="text-danger">*</span></label>
            <ImageAttachment class="custom-file-upload-wrapper" :preview="repairImagePreview || ''" :filename="repairImage ? repairImage.name : ''" :max-count="3"
              :invalid="Boolean(errors.problemImage)" @change="handleFileUpload" @remove="removeImage" />
          <small class="text-muted mt-2 d-block">{{ $t('sendReport.imageHint') }}</small>
            <div v-if="errors.problemImage" class="text-danger mt-2">
              {{ errors.problemImage }}
            </div>
          </div>

          <div class="mb-3">
            <label class="lbl">{{ $t('sendReport.workArea') }} <span class="text-danger">*</span></label>
            <div class="area-grid">
              <label class="area-card" :class="{ active: formData.locationType === 'building' }">
                <input type="radio" value="building" v-model="formData.locationType" class="sr-only" :name="'request-location-modal-' + _uid" />
                <div class="mb-1 area-card-icon">
                  <CIcon name="cil-building" size="xl" />
                </div>
                <div>
                  <strong>{{ $t('sendReport.insideBuilding') }}</strong><br />
                  <small class="text-muted">{{ $t('sendReport.insideBuildingSub') }}</small>
                </div>
                <div class="check">✓</div>
              </label>
              <label class="area-card" :class="{ active: formData.locationType === 'outdoor' }">
                <input type="radio" value="outdoor" v-model="formData.locationType" class="sr-only" :name="'request-location-modal-' + _uid" />
                <div class="mb-1 area-card-icon">
                  <CIcon name="cil-location-pin" size="xl" />
                </div>
                <div>
                  <strong>{{ $t('sendReport.outsideBuilding') }}</strong><br />
                  <small class="text-muted">{{ $t('sendReport.outsideBuildingSub') }}</small>
                </div>
                <div class="check">✓</div>
              </label>
            </div>
            <div v-if="errors.locationType" class="text-danger mt-2">
              {{ errors.locationType }}
            </div>
          </div>

          <transition name="fade" mode="out-in">
            <div v-if="formData.locationType === 'building'" class="sub-box">
              <div class="d-flex align-items-start">
                <div class="flex-grow-1" style="min-width: 0;">
                  <label class="lbl">{{ $t('sendReport.buildingRoom') }} <span class="text-danger">*</span></label>
                  <div class="position-relative">
                    <input type="text" ref="buildingInput" class="form-control" v-model="buildingSearchDetails"
                      :placeholder="$t('sendReport.buildingPlaceholder')" @input="buildingActiveIndex = -1; onBuildingInputChange()"
                      @focus="showBuildingSuggestions = true; buildingActiveIndex = -1" @blur="showBuildingSuggestions = false; buildingActiveIndex = -1"
                    @keydown="onBuildingKeydown" v-listbox-escape="closeBuildingSuggestions" role="combobox" :aria-controls="'building-modal-' + _uid" :aria-activedescendant="buildingSuggestionsVisible && buildingActiveIndex >= 0 ? 'building-modal-' + _uid + '-' + buildingActiveIndex : null" aria-autocomplete="list" :aria-expanded="String(Boolean(buildingSuggestionsVisible))" :aria-label="$t('sendReport.buildingRoom')"
                      :class="{ 'is-invalid': errors.buildingId }" autocomplete="off">
                    <ul
                      v-if="buildingSuggestionsVisible"
                      :id="'building-modal-' + _uid" role="listbox" :aria-label="$t('sendReport.buildingRoom')"
                    class="list-group position-absolute w-100 shadow bg-white"
                      style="z-index: 1050; max-height: 250px; overflow-y: auto; background-color: white;">
                      <li v-for="(b, index) in buildingSuggestions" :key="b._id"
                        class="list-group-item list-group-item-action cursor-pointer py-2 bg-white keyboard-listbox-option"
                      :id="'building-modal-' + _uid + '-' + index" role="option" :aria-selected="String(buildingActiveIndex === index)" :data-keyboard-active="buildingActiveIndex === index"
                        @mousedown.prevent="selectBuildingSuggestion(b)" style="cursor: pointer;">
                        <i class="cil-building mr-2 text-primary"></i>
                        <span><strong>{{ getBuildingDisplayName(b) }}</strong></span>
                      </li>
                    </ul>
                    <div
                      v-if="requiresRoom && buildingSearchDetails && (!buildingSearchDetails.includes('-') || buildingSearchDetails.split('-').filter(Boolean).length < 2)"
                      class="text-danger small mt-1 ml-1">
                      {{ $t('sendReport.buildingFormatHint') }}
                    </div>
                    <div v-if="selectedBuilding" class="text-success small mt-1 ml-1">
                      <i class="cil-check-circle"></i> {{ $t('sendReport.buildingFound') }} {{
                        getBuildingDisplayName(selectedBuilding)
                      }}
                    </div>
                    <CAlert v-if="isPropertyLocation" color="info" class="d-flex align-items-center mt-2 mb-0 py-2">
                      <CIcon name="cil-info" class="mr-2 flex-shrink-0" />
                      <small>{{ $t('sendReport.propertySectionNotice') }}</small>
                    </CAlert>
                  </div>
                  <div v-if="errors.buildingId" class="invalid-feedback d-block">
                    {{ errors.buildingId }}
                  </div>
                </div>
              </div>
            </div>
            <div v-else-if="formData.locationType === 'outdoor'" class="sub-box">
              <label class="lbl">{{ $t('sendReport.pinLocation') }} <span class="text-danger">*</span></label>
              <div class="border rounded overflow-hidden">
                <GoogleMapsPicker v-model="outdoorLocation" @input="onLocationChange" />
              </div>
              <div v-if="errors.location" class="text-danger mt-2">
                {{ errors.location }}
              </div>
            </div>
          </transition>

          <hr class="my-4 border-dashed" />

          <div class="d-flex align-items-center mb-4">
            <div class="icon-box bg-gradient-primary mr-3">
              <span class="text-white">
                <CIcon name="cil-user" size="xl" />
              </span>
            </div>
            <div>
              <h5 class="m-0 font-weight-bold">{{ $t('sendReport.reporterSection') }}</h5>
              <small class="text-muted">{{ $t('sendReport.reporterHint') }}</small>
            </div>
          </div>

          <div class="mb-4">
            <CRow>
              <CCol col="12" md="6" class="mb-3">
                <div class="mb-3">
                  <label class="lbl">{{ $t('sendReport.reporterName') }}</label>
                  <CInput id="reporterNameInput" name="reporterName"
                    :value="`${currentUser.name} ${currentUser.surname}`" class="mb-0" plaintext readonly />
                </div>
                <div class="mb-3">
                  <label class="lbl" for="affiliationInputDesktop">{{ $t('sendReport.affiliation') }}</label>
                  <CInput id="affiliationInputDesktop" :value="resolvedAffiliationName"
                    class="mb-0" plaintext readonly />
                </div>
                <div class="mb-3">
                  <label class="lbl" for="onBehalfOfInput">{{ $t('sendReport.onBehalfLabel') }}</label>
                  <CInput id="onBehalfOfInput" name="onBehalfOf" v-model="formData.onBehalfOf"
                    :placeholder="$t('sendReport.onBehalfPlaceholder')" autocomplete="off" />
                </div>
              </CCol>
              <CCol col="12" md="6" class="mb-3">
                <label class="lbl" for="personalPhoneInput">
                  {{ $t('sendReport.personalPhone') }} <span class="text-danger">*</span>
                </label>
                <CInput id="personalPhoneInput" name="personalPhone" v-model="formData.personalPhone"
                  :placeholder="$t('sendReport.phonePlaceholderMobile')" maxlength="10"
                  @input="formData.personalPhone = formData.personalPhone.replace(/[^0-9]/g, '')"
                  :class="{ 'is-invalid': errors.personalPhone }" />
                <div v-if="errors.personalPhone" class="invalid-feedback d-block">
                  {{ errors.personalPhone }}
                </div>
              </CCol>
            </CRow>
            <CRow class="mt-2">
              <CCol col="12" md="6" class="mb-3">
                <label class="lbl" for="telephoneWorkInput">
                  {{ $t('sendReport.workPhone') }}
                </label>
                <CInput id="telephoneWorkInput" name="telephoneWork" v-model="formData.telephoneWork"
                  :placeholder="$t('sendReport.phonePlaceholderWork')" maxlength="10"
                  @input="formData.telephoneWork = formData.telephoneWork.replace(/[^0-9]/g, '')"
                  :class="{ 'is-invalid': errors.telephoneWork }" />
                <div v-if="errors.telephoneWork" class="invalid-feedback d-block">
                  {{ errors.telephoneWork }}
                </div>
              </CCol>
              <CCol col="12" md="6" class="mb-3">
                <label class="lbl">{{ $t('sendReport.availability') }} <span v-if="formData.locationType !== 'outdoor'"
                    class="text-danger">*</span></label>
                <div class="date-time-container">
                  <div class="mb-2">
                    <label class="time-label mb-1" for="datePickerInputDesktop">{{ $t('sendReport.dateLabel') }}</label>
                    <div
                      class="date-display-wrapper"
                      :class="{ 'is-invalid': errors.availableTime }"
                      @click="openDatePicker('datePickerDesktop')"
                    >
                      <input
                        type="text"
                        class="date-display-input"
                        :value="displaySelectedDate"
                        :placeholder="datePlaceholder"
                        readonly
                        @keydown.enter.prevent="openDatePicker('datePickerDesktop')"
                        @keydown.space.prevent="openDatePicker('datePickerDesktop')"
                      />
                      <span class="date-picker-icon" :title="$t('sendReport.pickDate') || 'เลือกวันที่'">
                        <CIcon name="cil-calendar" />
                      </span>
                      <input
                        ref="datePickerDesktop"
                        id="datePickerInputDesktop"
                        name="selectedDate"
                        type="date"
                        v-model="formData.selectedDate"
                        :min="minDate"
                        class="date-native-hidden"
                        tabindex="-1"
                        aria-hidden="true"
                        @change="updateAvailableTime"
                      />
                    </div>
                  </div>
                  <div class="time-range-container">
                    <!-- เวลาเริ่มต้น -->
                    <div class="time-input-group">
                      <label class="time-label" for="startTimeInputDesktop">
                        {{ $t('sendReport.startLabel') }}
                      </label>

                      <div class="time-picker-wrapper" @click="triggerTimePicker('startTime')">
                        <span class="time-picker-icon" :title="$t('sendReport.pickTime') || 'เลือกเวลา'">
                          <CIcon name="cil-clock" />
                        </span>
                        <input id="startTimeInputDesktop" name="startTime" type="text" readonly role="button" aria-haspopup="dialog" :aria-expanded="String(showTimePickerModal && activeTimePickerField === 'startTime')" @keydown.enter.prevent="triggerTimePicker('startTime')" @keydown.space.prevent="triggerTimePicker('startTime')"
                          :value="formData.startTime || '--:--'" class="form-control time-input text-center"
                          :class="{ 'is-invalid': errors.availableTime }" />
                      </div>
                    </div>

                    <div class="time-separator">-</div>

                    <!-- เวลาสิ้นสุด -->
                    <div class="time-input-group">
                      <label class="time-label" for="endTimeInputDesktop">
                        {{ $t('sendReport.endLabel') }}
                      </label>

                      <div class="time-picker-wrapper" :class="{ 'disabled': !formData.startTime }" @click="triggerTimePicker('endTime')">
                        <span class="time-picker-icon" :title="$t('sendReport.pickTime') || 'เลือกเวลา'">
                          <CIcon name="cil-clock" />
                        </span>
                        <input id="endTimeInputDesktop" name="endTime" type="text" readonly role="button" aria-haspopup="dialog" :aria-expanded="String(showTimePickerModal && activeTimePickerField === 'endTime')" @keydown.enter.prevent="triggerTimePicker('endTime')" @keydown.space.prevent="triggerTimePicker('endTime')"
                          :value="formData.endTime || '--:--'" class="form-control time-input text-center"
                          :class="{ 'is-invalid': errors.availableTime }" :disabled="!formData.startTime" />
                      </div>
                    </div>
                  </div>
                </div>
                <small class="text-muted">{{ $t('sendReport.availabilityHint') }}</small>
                <div v-if="errors.availableTime" class="invalid-feedback d-block">
                  {{ errors.availableTime }}
                </div>
              </CCol>
            </CRow>
          </div>
        </div>
      </CForm>
    </CModalBody>

    <template #footer>
      <div class="d-flex w-100">
        <CButton color="secondary" variant="outline" class="request-cancel-btn flex-grow-1 mr-2 font-weight-bold"
          :class="{ 'admin-action-btn': isAdminStandard }" @click="closeModal"
          :disabled="isSubmitting">
          {{ $t('sendReport.cancel') }}
        </CButton>
        <CButton color="success" class="request-confirm-btn flex-grow-1 font-weight-bold text-white" @click="sendReport"
          :disabled="isSubmitting">
          <span v-if="!isSubmitting">
            {{ $t('sendReport.confirm') }}
          </span>
          <span v-else>
            <span class="spinner-border spinner-border-sm mr-1"></span> {{ $t('sendReport.submitting') }}
          </span>
        </CButton>
      </div>
    </template>
  </CModal>

  <!-- 24-Hour Circular Clock Wheel Picker Modal -->
  <CircularTimePickerModal
    :show.sync="showTimePickerModal"
    :value="activeTimePickerField === 'startTime' ? formData.startTime : formData.endTime"
    :title="activeTimePickerField === 'startTime' ? ($t('sendReport.startLabel') || 'เลือกเวลาเริ่มต้น') : ($t('sendReport.endLabel') || 'เลือกเวลาสิ้นสุด')"
    :min-time="activeTimePickerField === 'startTime' ? '08:00' : (formData.startTime || '08:00')"
    :max-time="'16:00'"
    :disabled-start-minutes="activeTimePickerField === 'endTime' && formData.startTime ? timeToMinutes(formData.startTime) : null"
    @select="onTimePickerSelected"
  />

</div>
</template>

<script>
import listboxEscape from '@/directives/listboxEscape';
import keyboardActivate from '@/directives/keyboardActivate';
import CModal from '@/components/common/AccessibleModal.vue';
import ImageAttachment from "@/components/common/ImageAttachment.vue";
import { compressImageToMax1MB, isImageFile } from "@/utils/imageCompressor";
import Vue from "vue";
import GoogleMapsPicker from "./UGoogleMapsPicker.vue";
import Multiselect from 'vue-multiselect';
import CircularTimePickerModal from "@/components/common/CircularTimePickerModal.vue";
import { mapGetters } from "vuex";
import 'vue-multiselect/dist/vue-multiselect.min.css';
import Swal from "sweetalert2";
import { formatAdminSwalButton } from "@/utils/sweetalertButtonHelper";
import { isVerifiedBlendataAffiliation } from "@/utils/affiliation";
import Service from "@/service/api";
import { translateSection, translateJobType, JOB_TYPE_TRANSLATIONS } from "@/utils/categoryi18n";
import {
  deduplicateCategoryOptions,
  isCategoryActive,
  filterCategoriesByQuery
} from "@/utils/repairTaxonomy";
import {
  matchesBuildingQuery,
  getBuildingEnglishName,
  getBuildingLocalizedName,
  requiresBuildingRoom,
  normalizeQuery,
  isPropertyLocationBuilding
} from "@/utils/buildingBilingual";




export default {
  name: "SendReport",
  directives: { keyboardActivate, listboxEscape },
  components: { CModal,
    ImageAttachment,
    GoogleMapsPicker,
    Multiselect,
    CircularTimePickerModal
  },
  props: {
    value: Boolean,
    displayMode: {
      type: String,
      default: 'modal'
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    adminButtonStandard: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    localModal: false,
    isSubmitting: false,
    outdoorLocation: {},
    repairImage: null,
    categories: [],
    buildings: [],
    selectedBuilding: null,
    appUrl: process.env.VUE_APP_APP_URL || "",
    currentStep: 1,
    isMobileViewport: window.innerWidth <= 768,
    loadedAffiliation: null,
    unratedCompletedTickets: [],
    selectedCategoryFilter: 'all',
    showIconGuideModal: false,
    brokenCustomIcons: {},
    formData: {
      selectedBuildingObject: null,
      personalPhone: "",
      telephoneWork: "",
      onBehalfOf: "",
      categoryType: "predefined", // NEW: 'predefined' or 'other'
      selectedSubCategory: null, // CHANGED: now a single object
      otherCategoryText: "", // NEW: for other category input
      selectedDate: "", // NEW: Date picker
      problemDetails: "",
      assetCode: "",
      availableTime: "",
      startTime: "",
      endTime: "",
      locationType: "",
      buildingId: "",
      roomName: "",
      latitude: null,
      longitude: null,
      problemImage: "",
    },
    repairImagePreview: "",
    errors: {},
    imageError: "",
    buildingSearchDetails: "", // NEW: For datalist input model
    categorySearchQuery: "",
    showBuildingSuggestions: false,
    buildingActiveIndex: -1,
    isBuildingSelected: false,
    isAndroid: false,
    showTimePickerModal: false,
    activeTimePickerField: 'startTime',
    loadedAffiliation: null,
  }),
  computed: {
    buildingSuggestionsVisible() {
      return this.showBuildingSuggestions && this.buildingSuggestions.length > 0 &&
        (!this.formData.buildingId || (this.requiresRoom && !this.buildingSearchDetails.includes('-')));
    },
    ...mapGetters("auth", ["profile"]),
    isAdminStandard() {
      if (this.adminButtonStandard) return true;
      if (this.$route?.meta?.roles && Array.isArray(this.$route.meta.roles)) {
        if (this.$route.meta.roles.includes('admin')) return true;
      }
      const authUser = this.$store?.state?.auth?.user || this.profile;
      if (authUser?.role === 'admin') return true;
      return Boolean(this.$route?.path && this.$route.path.startsWith('/admin/'));
    },
    // เวลาราชการ: เวลาเริ่มต้น 08:00 ถึง 15:30 น. (เพื่อให้อย่างน้อยมีเวลาทำงานจนถึง 16:00 น.)
    timeOptions() {
      const options = [];
      const startHour = 8;
      const endHour = 15;

      for (let hour = startHour; hour <= endHour; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
          const hh = String(hour).padStart(2, "0");
          const mm = String(minute).padStart(2, "0");

          options.push(`${hh}:${mm}`);
        }
      }

      return options;
    },

    // เวลาสิ้นสุด: 08:30 ถึง 16:00 น. และต้องมากกว่าเวลาเริ่มต้น
    endTimeOptions() {
      const allEndTimes = [];
      const startHour = 8;
      const endHour = 16;

      for (let hour = startHour; hour <= endHour; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
          if (hour === 8 && minute === 0) continue; // เร็วสุดคือ 08:30
          if (hour === 16 && minute > 0) continue; // ช้าสุดคือ 16:00
          const hh = String(hour).padStart(2, "0");
          const mm = String(minute).padStart(2, "0");

          allEndTimes.push(`${hh}:${mm}`);
        }
      }

      if (!this.formData.startTime) {
        return allEndTimes;
      }

      const startMinutes = this.timeToMinutes(this.formData.startTime);

      return allEndTimes.filter((time) => {
        return this.timeToMinutes(time) > startMinutes;
      });
    },
    currentUser() {
      return this.profile || {
        _id: '',
        name: this.$t('sendReport.defaultFirstName'),
        surname: this.$t('sendReport.defaultLastName'),
        email: ''
      };
    },

    resolvedAffiliation: {
      get() {
        if (this.loadedAffiliation) {
          return this.loadedAffiliation;
        }
        const aff = this.currentUser?.affiliation;
        if (aff && typeof aff === 'object') {
          return aff;
        }
        return null;
      },
      set(val) {
        this.loadedAffiliation = val;
      }
    },

    resolvedAffiliationName() {
      return this.resolvedAffiliation?.name || this.$t('sendReport.affiliationNotSpecified');
    },

    hasVerifiedUsableAffiliation() {
      const aff = this.resolvedAffiliation;
      return Boolean(
        aff &&
        isVerifiedBlendataAffiliation(aff) &&
        this.currentUser?.affiliationVerifiedAt
      );
    },

    affiliationStatusMessage() {
      const status = this.currentUser?.personClassResolutionStatus;
      if (status === 'conflict') {
        return this.$t('sendReport.conflictAffiliationWarning');
      }
      const isVerified = (typeof this.hasVerifiedUsableAffiliation === 'boolean')
        ? this.hasVerifiedUsableAffiliation
        : Boolean(
            this.resolvedAffiliation &&
            isVerifiedBlendataAffiliation(this.resolvedAffiliation) &&
            this.currentUser?.affiliationVerifiedAt
          );

      if (!this.resolvedAffiliation) {
        if (status === 'source_unavailable') {
          return this.$t('sendReport.outageAffiliationWarning');
        }
        return this.$t('sendReport.unverifiedAffiliationWarning');
      }
      if (!isVerified) {
        return this.$t('sendReport.unverifiedAffiliationWarning');
      }
      return '';
    },

    isRatingLocked() {
      if (this.isAdminStandard || this.currentUser?.role === 'admin') return false;
      return Array.isArray(this.unratedCompletedTickets) && this.unratedCompletedTickets.length > 0;
    },

    firstUnratedTicket() {
      return this.isRatingLocked ? this.unratedCompletedTickets[0] : null;
    },

    ratingLockMessage() {
      if (!this.isRatingLocked) return '';
      const count = this.unratedCompletedTickets.length;
      const ticketNo = this.firstUnratedTicket?.ticketNumber ? ` (${this.firstUnratedTicket.ticketNumber})` : '';
      return this.$t('sendReport.ratingLockMessage', { count, ticketNo }) ||
        `คุณมีงานซ่อมที่เสร็จสิ้นแล้วรอการประเมินความพึงพอใจ ${count} รายการ${ticketNo} กรุณาประเมินผลงานก่อนแจ้งซ่อมใหม่`;
    },

    isSubmissionBlocked() {
      if (this.isRatingLocked) return true;
      const status = this.currentUser?.personClassResolutionStatus;
      if (status === 'conflict') return true;
      const isVerified = (typeof this.hasVerifiedUsableAffiliation === 'boolean')
        ? this.hasVerifiedUsableAffiliation
        : Boolean(
            this.resolvedAffiliation &&
            isVerifiedBlendataAffiliation(this.resolvedAffiliation) &&
            this.currentUser?.affiliationVerifiedAt
          );

      if (status === 'source_unavailable' && isVerified) {
        return false;
      }
      if (!isVerified) return true;
      return false;
    },

    maintenanceGroups() {
      const lang = this.$store?.state?.locale?.lang || 'th'

      const groups = this.categories
        .filter(cat => cat.isActive !== false && Array.isArray(cat.subCategories) && cat.subCategories.some(sub => sub.isActive !== false))
        .map(cat => {
          const rawSection = cat.departmentName || cat.name || cat.code
          const sectionLabel = translateSection(cat, lang)
          const seen = new Set();
          const options = [];
          for (const sub of cat.subCategories) {
            if (sub.isActive === false) continue;
            if (sub.department && sub.department.isActive === false) continue;
            if (seen.has(sub.name)) continue;
            seen.add(sub.name);
            options.push({
              value: sub._id || sub.name,
              name: sub.name,
              subCategoryId: sub._id,
              label: translateJobType(sub, lang),
              categoryId: cat._id,
              categoryName: sectionLabel,
              categoryCode: cat.code || '',
              slaHours: sub.slaHours,
              icon: sub.icon || null
            });
          }
          return {
            categoryId: cat._id,
            label: sectionLabel,
            options
          };
        });

      const otherLabel = translateSection('อื่น ๆ', lang)
      groups.push({
        categoryId: 'other',
        label: otherLabel,
        options: [{
          value: 'other',
          label: otherLabel,
          categoryId: 'other',
          categoryName: otherLabel,
          categoryCode: 'OTHER',
          slaHours: 24
        }]
      });

      return groups;
    },

    isEquipmentRelated() {
      // Asset code field removed as requested for Computer & Equipment and Printer & Scanner
      return false;
    },

        deduplicatedCategories() {
      const lang = this.$store?.state?.locale?.lang || 'th';
      return deduplicateCategoryOptions(this.allSubCategoryOptions, lang);
    },

    frequentlyUsedCategories() {
      return this.deduplicatedCategories.filter(c => c.isPopular).slice(0, 7);
    },

    filteredCategories() {
      return filterCategoriesByQuery(this.deduplicatedCategories, this.categorySearchQuery);
    },

    selectedCategoryDisplayLabel() {
      if (!this.formData.selectedSubCategory) return '';
      const match = this.deduplicatedCategories.find(c => isCategoryActive(c, this.formData.selectedSubCategory));
      return match ? match.label : (this.formData.selectedSubCategory.label || this.formData.selectedSubCategory.name);
    },

    allSubCategoryOptions() {
      return this.maintenanceGroups.flatMap(group => group.options);
    },

    popularOptions() {
      const criteria = [
        { rank: 1, test: (s) => /ปรับอากาศ|แอร์|air condition|hvac/i.test(s) },
        { rank: 2, test: (s) => /หลอดไฟ|แสงสว่าง|lighting|lamp|bulb/i.test(s) },
        { rank: 3, test: (s) => /ปลั๊ก|สวิตช์|ไฟฟ้า|electricity|power/i.test(s) },
        { rank: 4, test: (s) => /ท่อ|น้ำรั่ว|ก๊อก|ท่อน้ำ|plumb|pipe|leak|tap/i.test(s) },
        { rank: 5, test: (s) => /น้ำไม่ไหล|สุขาภิบาล|ชักโครก|water supply|toilet/i.test(s) },
        { rank: 6, test: (s) => /wi-?fi|เน็ต|อินเทอร์เน็ต|internet|network|lan/i.test(s) },
        { rank: 7, test: (s) => /คอมพิวเตอร์|เครื่องคอม|pc|computer|monitor|จอ/i.test(s) },
        { rank: 8, test: (s) => /พิมพ์|ปริ้น|printer|print|scanner/i.test(s) },
        { rank: 9, test: (s) => /ประตู|ลูกบิด|หน้าต่าง|กลอน|door|lock|window/i.test(s) },
      ];

      const popular = [];
      const usedValues = new Set();

      for (const item of criteria) {
        const matched = this.allSubCategoryOptions.find(opt => 
          !usedValues.has(opt.value) && opt.value !== 'other' && item.test(opt.label + ' ' + (opt.name || ''))
        );
        if (matched) {
          usedValues.add(matched.value);
          popular.push({ ...matched, popularRank: item.rank });
        }
      }

      if (popular.length < 7) {
        for (const opt of this.allSubCategoryOptions) {
          if (!usedValues.has(opt.value) && opt.value !== 'other') {
            usedValues.add(opt.value);
            popular.push({ ...opt, popularRank: popular.length + 1 });
            if (popular.length >= 8) break;
          }
        }
      }

      return popular.slice(0, 9);
    },

    popularOptionValues() {
      return new Set(this.popularOptions.map(o => o.value));
    },

    displayJobOptions() {
      if (this.selectedCategoryFilter === 'popular') {
        return this.popularOptions;
      }
      if (this.selectedCategoryFilter !== 'all') {
        return this.allSubCategoryOptions.filter(opt => opt.categoryId === this.selectedCategoryFilter);
      }
      
      const popular = [];
      const others = [];
      for (const opt of this.allSubCategoryOptions) {
        if (this.isOptionPopular(opt)) {
          popular.push(opt);
        } else {
          others.push(opt);
        }
      }
      return [...popular, ...others];
    },

    categoryFilterTabs() {
      const tabs = [
        { id: 'all', label: 'ทั้งหมด (All)' },
        { id: 'popular', label: `🔥 ยอดนิยม (${this.popularOptions.length})` }
      ];
      for (const g of this.maintenanceGroups) {
        if (g.categoryId !== 'other' && g.options.length > 0) {
          tabs.push({ id: g.categoryId, label: g.label });
        }
      }
      return tabs;
    },

    minDate() {
      const today = new Date();
      const yyyy = today.getFullYear();
      let mm = today.getMonth() + 1; // Months start at 0
      let dd = today.getDate();

      if (dd < 10) dd = '0' + dd;
      if (mm < 10) mm = '0' + mm;

      return yyyy + '-' + mm + '-' + dd;
    },

    displaySelectedDate() {
      const val = this.formData.selectedDate;
      if (!val) return '';
      // val is YYYY-MM-DD, convert to DD/MM/YYYY
      const parts = val.split('-');
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return val;
    },

    datePlaceholder() {
      return this.$t('sendReport.datePlaceholder') || (this.$store.state.locale?.lang === 'en' ? 'dd/mm/yyyy' : 'วว/ดด/ปปปป');
    },

    buildingSuggestions() {
      const input = (this.buildingSearchDetails || "").trim();
      if (!input) return [];

      const maxSuggestions = 10;

      // 1. Direct match on full input (supports building names with hyphens like 'M-Store')
      let matched = this.buildings.filter(b => matchesBuildingQuery(b, input));

      // 2. If no match and input contains hyphen, check candidate before first hyphen
      if (matched.length === 0 && input.includes('-')) {
        const parts = input.split('-');
        const beforeDash = parts[0].trim();
        if (beforeDash) {
          matched = this.buildings.filter(b => matchesBuildingQuery(b, beforeDash));
        }
      }

      if (matched.length === 0) return [];

      const qNorm = normalizeQuery(input.split('-')[0].trim() || input);

      // Prioritize exact matches, then prefix matches
      matched.sort((a, b) => {
        const aCode = normalizeQuery(a.BUILDINGCODE || '');
        const bCode = normalizeQuery(b.BUILDINGCODE || '');
        const aEn = normalizeQuery(getBuildingEnglishName(a) || '');
        const bEn = normalizeQuery(getBuildingEnglishName(b) || '');

        const aExact = aCode === qNorm || aEn === qNorm;
        const bExact = bCode === qNorm || bEn === qNorm;
        if (aExact && !bExact) return -1;
        if (!aExact && bExact) return 1;

        const aPrefix = aCode.startsWith(qNorm) || aEn.startsWith(qNorm);
        const bPrefix = bCode.startsWith(qNorm) || bEn.startsWith(qNorm);
        if (aPrefix && !bPrefix) return -1;
        if (!aPrefix && bPrefix) return 1;

        return 0;
      });

      return matched.slice(0, maxSuggestions);
    },
    isPageMode() {
      return this.displayMode === 'page';
    },
    isWizardMode() {
      // The request page is intentionally a single, scrollable form on every
      // viewport. Keep the old wizard helpers available for the modal flow,
      // but do not split page mode into separate steps.
      return false;
    },
    currentStepTitle() {
      const keys = {
        1: 'sendReport.jobType',
        2: 'sendReport.problemLabel',
        3: 'sendReport.workArea'
      };
      return this.$t(keys[this.currentStep]);
    },
    requiresRoom() {
      return requiresBuildingRoom(this.selectedBuilding);
    },
    isPropertyLocation() {
      return isPropertyLocationBuilding(this.selectedBuilding) ||
             isPropertyLocationBuilding(this.buildingSearchDetails);
    },
  },
  watch: {
    '$store.state.locale.lang'(locale, previousLocale) {
      if (!this.selectedBuilding) return;
      const previous = getBuildingLocalizedName(this.selectedBuilding, previousLocale);
      const current = getBuildingLocalizedName(this.selectedBuilding, locale);
      const input = this.buildingSearchDetails || '';
      if (input.startsWith(previous)) {
        this.buildingSearchDetails = current + input.slice(previous.length);
      }
    },
    value(val) {
      this.localModal = val;
      if (val) {
        this.loadData();
      }
    },
    localModal(val) {
      this.$emit("input", val);
      if (!val) {
        this.resetForm();
      }
    },
    isEquipmentRelated(val) {
      if (!val) {
        this.formData.assetCode = "";
        if (this.errors && this.errors.assetCode) {
          this.$delete(this.errors, "assetCode");
        }
      }
    },
  },
  created() {
    this.localModal = this.value;
  },
  mounted() {
    this.isAndroid = /Android/i.test(navigator.userAgent);
    this.updateViewport();
    window.addEventListener('resize', this.updateViewport);
    this.loadData();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateViewport);
    if (this.repairImagePreview) {
      URL.revokeObjectURL(this.repairImagePreview);
    }
  },
  methods: {
    updateViewport() {
      this.isMobileViewport = window.innerWidth <= 768;
      if (!this.isWizardMode) {
        this.currentStep = 1;
      }
    },
    async loadData() {
      try {
        // Load categories
        const categoryResponse = await fetch(`${process.env.VUE_APP_APP_URL}/category`);
        const categoryResult = await categoryResponse.json();
        if (categoryResult.success) {
          this.categories = categoryResult.data;
        }

        // Load buildings
        const buildingResponse = await fetch(`${process.env.VUE_APP_APP_URL}/building`);
        const buildingResult = await buildingResponse.json();
        this.buildings = Array.isArray(buildingResult) ? buildingResult : (buildingResult.data || []);

        const affiliationResponse = await Service.user('get', null, { urlAppend: '/organization-units' });
        const organizationUnits = affiliationResponse.data?.data || [];
        const affiliation = this.currentUser?.affiliation;
        const affiliationId = affiliation?._id || affiliation || null;
        this.resolvedAffiliation = organizationUnits.find((unit) =>
          String(unit?._id || '') === String(affiliationId || '') &&
          isVerifiedBlendataAffiliation(unit)
        ) || null;

        // Auto-fill contact info
        await this.autoFillContactInfo();

      } catch (error) {
        console.error('Failed to load data:', error);
      }
    },

    async autoFillContactInfo() {
      if (!this.currentUser || !this.currentUser._id) return;

      // --- Step 1: Fill from Profile first (immediate, no extra API call) ---
      const profilePhone = this.currentUser?.mobile_phone || this.currentUser?.phone || this.currentUser?.tel;
      if (profilePhone && String(profilePhone).trim()) {
        this.formData.personalPhone = String(profilePhone).trim();
      }

      try {
        const userId = this.currentUser._id;

        // --- Step 2: Fetch ticket history for time preference + work phone ---
        const [activeRes, historyRes] = await Promise.all([
          Service.ticket('get', null, { urlAppend: `/user/${userId}/active` }),
          Service.ticket('get', null, { urlAppend: `/user/${userId}/history` })
        ]);

        const activeData = activeRes.data;
        const historyData = historyRes.data;

        let allTickets = [];
        if (activeData.success && Array.isArray(activeData.data)) {
          allTickets = allTickets.concat(activeData.data);
          if (!this.isAdminStandard && this.currentUser?.role !== 'admin') {
            this.unratedCompletedTickets = activeData.data.filter(t => t && t.status === 'done');
          }
        }
        if (historyData.success && Array.isArray(historyData.data)) {
          allTickets = allTickets.concat(historyData.data);
        }

        if (allTickets.length > 0) {
          // Sort by createdAt descending (newest first)
          allTickets.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

          const latestTicket = allTickets[0];

          if (latestTicket && latestTicket.userPart) {
            // Personal phone: use ticket only if profile had none
            if (!this.formData.personalPhone && latestTicket.userPart.personalPhone) {
              this.formData.personalPhone = latestTicket.userPart.personalPhone;
            }

            // Work phone always from ticket (not stored in profile)
            if (latestTicket.userPart.telephoneWork &&
              latestTicket.userPart.telephoneWork !== '-' &&
              latestTicket.userPart.telephoneWork !== '0000000000') {
              this.formData.telephoneWork = latestTicket.userPart.telephoneWork;
            }

            // Auto-fill available time from latest ticket
            if (latestTicket.userPart.availableTime && latestTicket.userPart.availableTime !== '00:00 - 00:00') {
              const timeMatch = latestTicket.userPart.availableTime.match(/(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})$/);
              if (timeMatch) {
                this.formData.startTime = timeMatch[1];
                this.formData.endTime = timeMatch[2];
                this.updateAvailableTime();
              }
            }
          }
        }
      } catch (error) {
        console.error("Error auto-filling contact info from ticket history:", error);
      }
    },

    onBuildingChange(selectedOption) {
      if (selectedOption) {
        this.formData.buildingId = selectedOption.value;
        this.selectedBuilding = this.buildings.find(
          b => String(b._id) === String(this.formData.buildingId)
        );
      } else {
        this.formData.buildingId = "";
        this.selectedBuilding = null;
      }

      this.formData.roomName = "";
      this.errors.buildingId = "";
      this.errors.roomName = "";
    },

    getBuildingEnglishName(b) {
      return getBuildingEnglishName(b);
    },

    getBuildingDisplayName(b) {
      return getBuildingLocalizedName(b, this.$store.state.locale.lang);
    },

    closeBuildingSuggestions() { this.showBuildingSuggestions = false; this.buildingActiveIndex = -1; },
    onBuildingKeydown(event) {
      if (event.isComposing) return;
      if (event.key === 'Escape') {
        if (this.showBuildingSuggestions) { event.preventDefault(); event.stopPropagation(); }
        this.showBuildingSuggestions = false;
        this.buildingActiveIndex = -1;
      } else if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        this.showBuildingSuggestions = true;
        const count = this.buildingSuggestions.length;
        this.buildingActiveIndex = count ? (this.buildingActiveIndex < 0
          ? (event.key === 'ArrowDown' ? 0 : count - 1)
          : (this.buildingActiveIndex + (event.key === 'ArrowDown' ? 1 : -1) + count) % count) : -1;
        this.$nextTick(() => {
          const option = document.getElementById(event.target.getAttribute('aria-activedescendant'));
          if (option && option.scrollIntoView) option.scrollIntoView({ block: 'nearest' });
        });
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (this.buildingSuggestionsVisible && this.buildingSuggestions[this.buildingActiveIndex]) {
          this.selectBuildingSuggestion(this.buildingSuggestions[this.buildingActiveIndex]);
          this.buildingActiveIndex = -1;
        }
      }
    },
    onBuildingInputChange() {
      // Reset logic
      this.formData.buildingId = "";
      this.selectedBuilding = null;
      this.errors.buildingId = "";
      this.showBuildingSuggestions = true;

      const input = (this.buildingSearchDetails || "").trim();
      this.formData.roomName = input; // Store full input in roomName initially

      if (!input) return;

      // 1. If input contains hyphen, split into building candidate and room candidate
      if (input.includes('-')) {
        const parts = input.split('-');
        let bestMatch = null;
        let bestRoom = "";

        // Try forming building code from increasing number of parts (greedy match for building code)
        for (let i = 1; i < parts.length; i++) {
          const codeCandidate = parts.slice(0, i).join('-').trim();
          const roomCandidate = parts.slice(i).join('-').trim();

          if (!codeCandidate) continue;

          const match = this.buildings.find(b => matchesBuildingQuery(b, codeCandidate));

          if (match) {
            bestMatch = match;
            bestRoom = roomCandidate;
          }
        }

        if (bestMatch && bestRoom) {
          const isProp = isPropertyLocationBuilding(bestMatch) || isPropertyLocationBuilding(input);
          this.selectedBuilding = bestMatch;
          this.formData.buildingId = bestMatch._id;
          this.formData.roomName = bestRoom;
          this.errors.buildingId = "";
          return;
        }
      }

      // 2. Direct match check on the entire input (handles property locations, exact codes like 'M-Store', etc.)
      const directMatch = this.buildings.find(b => matchesBuildingQuery(b, input));
      if (directMatch) {
        const roomOptional = !requiresBuildingRoom(directMatch);
        if (roomOptional) {
          this.formData.buildingId = directMatch._id;
          this.formData.roomName = directMatch.BUILDINGCODE || input;
          this.selectedBuilding = directMatch;
          this.errors.buildingId = "";
          return;
        } else {
          // Visual feedback for non-property building without room number
          this.selectedBuilding = directMatch;
          this.formData.roomName = "";
        }
      }
    },

    selectBuildingSuggestion(building) {
      const code = building.BUILDINGCODE || building.BUILDINGNAME || building.name || "";
      const label = getBuildingLocalizedName(building, this.$store.state.locale.lang);
      const isProp = isPropertyLocationBuilding(building) || isPropertyLocationBuilding(code);

      if (!requiresBuildingRoom(building)) {
        this.buildingSearchDetails = label;
        this.selectedBuilding = building;
        this.formData.buildingId = building._id;
        this.formData.roomName = code;
        this.errors.buildingId = "";
        this.showBuildingSuggestions = false;
        return;
      }

      this.buildingSearchDetails = `${label}-`;
      this.selectedBuilding = building;
      this.formData.buildingId = building._id;
      this.formData.roomName = "";
      this.errors.buildingId = "";
      this.showBuildingSuggestions = false;

      // Keep focus in input to type room number
      this.$nextTick(() => {
        if (this.$refs.buildingInput) {
          this.$refs.buildingInput.focus();
        }
      });
    },

    onRoomChange(event) {
      // Remove room validation since it's now a text input
      const value = event && event.target ? event.target.value : event;
      this.errors.roomName = "";
    },

    timeToMinutes(time) {
      if (!time) return -1;

      const [hour, minute] = time.split(":").map(Number);

      return (hour * 60) + minute;
    },

    triggerTimePicker(field) {
      if (field === 'endTime' || field === 'endTimeInput' || field === 'endTimeInputDesktop') {
        if (!this.formData.startTime) return;
        this.activeTimePickerField = 'endTime';
      } else {
        this.activeTimePickerField = 'startTime';
      }
      this.showTimePickerModal = true;
    },

    onTimePickerSelected(val) {
      if (this.activeTimePickerField === 'startTime') {
        this.formData.startTime = val;
        this.onStartTimeChange();
      } else if (this.activeTimePickerField === 'endTime') {
        this.formData.endTime = val;
        this.onEndTimeChange();
      }
    },

    onStartTimeChange() {
      this.errors.availableTime = "";

      // ถ้ามี endTime เดิมอยู่
      // แต่เวลานั้นน้อยกว่าหรือเท่ากับ startTime ใหม่
      // ให้ล้าง endTime ทันที
      if (
        this.formData.endTime &&
        this.timeToMinutes(this.formData.endTime) <=
        this.timeToMinutes(this.formData.startTime)
      ) {
        this.formData.endTime = "";
      }

      this.updateAvailableTime();
    },

    onEndTimeChange() {
      this.errors.availableTime = "";

      if (
        this.formData.startTime &&
        this.formData.endTime &&
        this.timeToMinutes(this.formData.endTime) <=
        this.timeToMinutes(this.formData.startTime)
      ) {
        this.formData.endTime = "";

        this.errors.availableTime =
          this.$t("sendReport.errEndAfterStart");

        this.formData.availableTime = "";

        return;
      }

      this.updateAvailableTime();
    },

    updateAvailableTime() {
      this.errors.availableTime = "";

      const selectedDate = this.formData.selectedDate;
      const startTime = this.formData.startTime;
      const endTime = this.formData.endTime;

      // ยังเลือกไม่ครบ
      if (!startTime || !endTime) {
        this.formData.availableTime = "";
        return;
      }

      const startMinutes = this.timeToMinutes(startTime);
      const endMinutes = this.timeToMinutes(endTime);

      // ป้องกันกรณีแก้ค่าโดย DevTools หรือค่าเก่าจากระบบ
      if (endMinutes <= startMinutes) {
        this.errors.availableTime =
          this.$t("sendReport.errEndAfterStart");

        this.formData.endTime = "";
        this.formData.availableTime = "";

        return;
      }

      let datePart = "";

      if (selectedDate) {
        // เติม T00:00:00 เพื่อไม่ให้ timezone ทำวันที่เลื่อน
        const date = new Date(`${selectedDate}T00:00:00`);

        if (this.$store.state.locale.lang === "en") {
          const dd = String(date.getDate()).padStart(2, '0');
          const mm = String(date.getMonth() + 1).padStart(2, '0');
          const yyyy = date.getFullYear();
          datePart = `${dd}/${mm}/${yyyy}` + " at ";
        } else {
          const dd = String(date.getDate()).padStart(2, '0');
          const mm = String(date.getMonth() + 1).padStart(2, '0');
          const yyyy = date.getFullYear() + 543; // แปลงเป็น พ.ศ.
          datePart = `${dd}/${mm}/${yyyy}` + " เวลา ";
        }
      }

      this.formData.availableTime =
        `${datePart}${startTime}\u00A0-\u00A0${endTime}`;
    },

    openDatePicker(refName) {
      const el = this.$refs[refName];
      if (!el) return;
      if (typeof el.showPicker === 'function') {
        try {
          el.showPicker();
          return;
        } catch (e) {
          // fallback
        }
      }
      el.focus();
    },


    onLocationChange(location) {
      this.formData.latitude = location.lat;
      this.formData.longitude = location.lng;
      this.errors.location = "";
    },

    compressImage(file, quality = 0.8, maxWidth = 1200) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
          const img = new Image();
          img.src = event.target.result;
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            let width = img.width;
            let height = img.height;

            if (width > height) {
              if (width > maxWidth) {
                height *= maxWidth / width;
                width = maxWidth;
              }
            } else {
              if (height > maxWidth) {
                width *= maxWidth / height;
                height = maxWidth;
              }
            }

            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob((blob) => {
              if (!blob) {
                reject(new Error('Compression failed'));
                return;
              }
              const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, ".jpg"), {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            }, 'image/jpeg', quality);
          };
          img.onerror = (err) => reject(err);
        };
        reader.onerror = (err) => reject(err);
      });
    },

    async handleFileUpload(event) {
      let files = [];
      if (event && event.files && Array.isArray(event.files)) {
        files = event.files;
      } else if (event && event.target && event.target.files) {
        files = Array.from(event.target.files);
      } else if (Array.isArray(event)) {
        files = event;
      } else if (event instanceof File || (event && event.name)) {
        files = [event];
      }

      this.repairImages = files;
      this.repairImage = files[0] || null;

      if (this.repairImagePreview) {
        URL.revokeObjectURL(this.repairImagePreview);
      }
      if (this.repairImage) {
        try {
          this.repairImagePreview = URL.createObjectURL(this.repairImage);
        } catch (e) {
          this.repairImagePreview = "";
        }
      } else {
        this.repairImagePreview = "";
      }
      this.imageError = "";
      this.$set(this.errors, 'problemImage', "");

      if (event && event.target && typeof event.target.blur === 'function') {
        event.target.blur();
      }
      if (document.activeElement && typeof document.activeElement.blur === 'function') {
        document.activeElement.blur();
      }
    },

    removeImage(idx) {
      if (typeof idx === 'number' && this.repairImages.length > 0) {
        this.repairImages.splice(idx, 1);
        this.repairImage = this.repairImages[0] || null;
      } else {
        if (this.repairImagePreview) {
          URL.revokeObjectURL(this.repairImagePreview);
          this.repairImagePreview = "";
        }
        this.repairImage = null;
        this.repairImages = [];
      }
      this.imageError = "";
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
      if (this.$refs.cameraInput) {
        this.$refs.cameraInput.value = "";
      }
      if (this.$refs.galleryInput) {
        this.$refs.galleryInput.value = "";
      }
      if (!this.repairImage && (!this.repairImages || this.repairImages.length === 0)) {
        this.$set(this.errors, 'problemImage', this.$t('sendReport.errAttachImage'));
      }
    },

    validateForm() {
      const errors = {};
      let isValid = true;

      // เพิ่มตัวเช็คความถูกต้องของเบอร์โทร
      const phoneRegex = /^\d{9,10}$/;

      if (!this.formData.personalPhone) {
        errors.personalPhone = this.$t('sendReport.errPhoneRequired');
        isValid = false;
      } else if (!phoneRegex.test(this.formData.personalPhone)) {
        errors.personalPhone = this.$t('sendReport.errPhoneDigits');
        isValid = false;
      }

      if (this.formData.telephoneWork && this.formData.telephoneWork.trim() !== '' && !phoneRegex.test(this.formData.telephoneWork)) {
        errors.telephoneWork = this.$t('sendReport.errPhoneDigits');
        isValid = false;
      }

      // Validate time range
      if (this.formData.locationType !== 'outdoor') {
        if (!this.formData.selectedDate) {
          errors.availableTime = this.$t('sendReport.errPickDate');
          isValid = false;
        } else if (!this.formData.startTime || !this.formData.endTime) {
          errors.availableTime = this.$t('sendReport.errPickTime');
          isValid = false;
        } else {
          // Recalculate and check one last time
          this.updateAvailableTime();
          if (!this.formData.availableTime) {
            // Check if it's because of time-range (end <= start)
            const startMin = this.formData.startTime.split(':').map(Number).reduce((h, m) => h * 60 + m);
            const endMin = this.formData.endTime.split(':').map(Number).reduce((h, m) => h * 60 + m);
            if (endMin <= startMin) {
              errors.availableTime = this.$t('sendReport.errEndAfterStart');
            } else {
              errors.availableTime = this.$t('sendReport.errPickTime');
            }
            isValid = false;
          }
        }
      }

      // Validate categories
      if (!this.formData.selectedSubCategory) {
        errors.subCategories = this.$t('sendReport.errPickJobType');
        isValid = false;
      } else if (this.formData.selectedSubCategory.value === 'other') {
        if (!this.formData.otherCategoryText || this.formData.otherCategoryText.trim() === '') {
          errors.otherCategoryText = this.$t('sendReport.errSpecifyOther');
          isValid = false;
        }
      }

      if (!this.formData.locationType) {
        errors.locationType = this.$t('sendReport.errWorkArea');
        isValid = false;
      }

      if (this.formData.locationType === 'building') {
        const inputStr = (this.buildingSearchDetails || "").trim();
        const isProperty = this.isPropertyLocation ||
          isPropertyLocationBuilding(this.selectedBuilding) ||
          isPropertyLocationBuilding(inputStr);

        if (!requiresBuildingRoom(this.selectedBuilding || this.buildings.find(b => matchesBuildingQuery(b, inputStr)))) {
          if (!this.formData.buildingId && this.selectedBuilding) {
            this.formData.buildingId = this.selectedBuilding._id;
          }
          if (!this.formData.buildingId) {
            const match = this.buildings.find(b => matchesBuildingQuery(b, inputStr));
            if (match) {
              this.formData.buildingId = match._id;
              this.selectedBuilding = match;
            }
          }

          if (!this.formData.buildingId) {
            errors.buildingId = this.$t('sendReport.errSelectBuilding');
            isValid = false;
          } else {
            // For property locations, room number is optional. Default roomName to building code or input if not specified.
            if (!this.formData.roomName || this.formData.roomName.trim() === '') {
              this.formData.roomName = inputStr || (this.selectedBuilding && this.selectedBuilding.BUILDINGCODE) || "ทรัพย์สิน";
            }
          }
        } else {
          const parts = inputStr.split('-');
          if (!inputStr.includes('-') || parts.filter(p => p.trim()).length < 2 || !this.formData.roomName || inputStr.endsWith('-')) {
            errors.buildingId = this.$t('sendReport.errBuildingFormat');
            isValid = false;
          } else if (!this.formData.buildingId) {
            errors.buildingId = this.$t('sendReport.errSelectBuilding');
            isValid = false;
          } else {
            this.formData.roomName = inputStr.trim();
          }
        }
      } else if (this.formData.locationType === 'outdoor') {
        if (!this.formData.latitude || !this.formData.longitude) {
          errors.location = this.$t('sendReport.errMapPin');
          isValid = false;
        }
      }

      if (this.isEquipmentRelated && (!this.formData.assetCode || this.formData.assetCode.trim() === '')) {
        errors.assetCode = this.$t('sendReport.errAssetCode');
        isValid = false;
      }

      if (!this.formData.problemDetails || this.formData.problemDetails.trim() === '') {
        errors.problemDetails = this.$t('sendReport.errProblem');
        isValid = false;
      }

      // Check Image
      if (this.imageError) {
        errors.problemImage = this.imageError;
        isValid = false;
      } else if (!this.repairImage) {
        errors.problemImage = this.$t('sendReport.errAttachImage');
        isValid = false;
      }

      this.errors = errors;
      return isValid;
    },

    async uploadImage() {
      const filesToUpload = (this.repairImages && this.repairImages.length > 0)
        ? this.repairImages
        : (this.repairImage ? [this.repairImage] : []);

      if (filesToUpload.length === 0) return { filename: null, filenames: [] };

      const formData = new FormData();
      filesToUpload.slice(0, 3).forEach((file, index) => {
        formData.append('images', file, file.name || `problem_${index + 1}.jpg`);
      });

      try {
        const response = await fetch(`${process.env.VUE_APP_APP_URL}/upload`, {
          method: 'POST',
          body: formData
        });
        const result = await response.json();
        if (result.success) {
          const filenames = result.filenames || (result.filename ? [result.filename] : []);
          return {
            filename: result.filename || filenames[0],
            filenames: filenames
          };
        }
        throw new Error('Upload failed');
      } catch (error) {
        console.error('Image upload error:', error);
        throw error;
      }
    },

    escapeConfirmText(value) {
      const element = document.createElement('div');
      element.textContent = value == null ? '-' : String(value);
      return element.innerHTML;
    },

    scrollToFirstError() {
      this.$nextTick(() => {
        // Priority order of error fields from top to bottom of the form
        const errorFieldSelectors = [
          // 1. Job type
          { key: 'subCategories', sel: '.repair-category-groups, .job-type-grid' },
          { key: 'otherCategoryText', sel: 'input[name="otherCategoryText"], .is-invalid[placeholder*="ระบุ"]' },
          // 2. Asset code
          { key: 'assetCode', sel: 'input[name="assetCode"], .is-invalid[placeholder*="ครุภัณฑ์"]' },
          // 3. Problem details
          { key: 'problemDetails', sel: 'textarea[name="problemDetails"], textarea.is-invalid, textarea' },
          // 4. Image
          { key: 'problemImage', sel: '.custom-file-upload-wrapper, .upload-button' },
          // 5. Work area
          { key: 'locationType', sel: '.area-grid' },
          // 6. Building / Room
          { key: 'buildingId', sel: 'input[ref="buildingInput"], input[name="buildingSearchDetails"], .form-control.is-invalid' },
          // 7. Outdoor map pin
          { key: 'location', sel: '.vue-map-container, .border.rounded.overflow-hidden' },
          // 8. Personal phone
          { key: 'personalPhone', sel: '#personalPhoneInput, #personalPhoneInputDesktop, input[name="personalPhone"]' },
          // 10. Work phone
          { key: 'telephoneWork', sel: '#telephoneWorkInput, #telephoneWorkInputDesktop, input[name="telephoneWork"]' },
          // 11. Availability time
          { key: 'availableTime', sel: '#datePickerInput, #datePickerInputDesktop, #startTimeInput, .date-time-container' }
        ];

        let targetEl = null;

        for (const item of errorFieldSelectors) {
          if (this.errors && this.errors[item.key]) {
            const el = this.$el ? this.$el.querySelector(item.sel) : document.querySelector(item.sel);
            if (el) {
              targetEl = el;
              break;
            }
          }
        }

        // Fallback: search for any invalid feedback or is-invalid element
        if (!targetEl && this.$el) {
          targetEl = this.$el.querySelector('.is-invalid, .invalid-feedback:not(:empty), .text-danger.mt-2');
        }

        if (targetEl) {
          targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });

          const focusable = targetEl.matches('input, select, textarea')
            ? targetEl
            : targetEl.querySelector('input, select, textarea');

          if (focusable && typeof focusable.focus === 'function') {
            setTimeout(() => {
              focusable.focus();
            }, 350);
          }
        }
      });
    },

    showRatingLockAlert() {
      Swal.fire({
        icon: "warning",
        title: this.$t('sendReport.ratingLockTitle') || "กรุณาประเมินผลงานก่อนแจ้งซ่อมใหม่",
        text: this.ratingLockMessage,
        showCancelButton: true,
        confirmButtonText: this.$t('sendReport.goToRating') || "ไปที่แบบประเมิน",
        cancelButtonText: this.$t('sendReport.cancel') || "ยกเลิก",
        confirmButtonColor: "#1e40af",
      }).then((result) => {
        if (result && result.isConfirmed) {
          this.handleGoToRating();
        }
      });
    },

    async sendReport() {
      if (this.isRatingLocked) {
        this.showRatingLockAlert();
        return;
      }

      if (this.isSubmissionBlocked) {
        Swal.fire({
          icon: "warning",
          title: this.$t('sendReport.swalErrorTitle') || "ไม่สามารถส่งคำขอซ่อมได้",
          text: this.affiliationStatusMessage,
          confirmButtonColor: "#1e40af",
        });
        return;
      }

      if (!this.validateForm()) {
        this.scrollToFirstError();
        Swal.fire({
          icon: "warning",
          title: this.$t('sendReport.swalIncompleteTitle'),
          text: this.$t('sendReport.swalIncompleteText'),
          confirmButtonColor: "#f9b115",
        }).then(() => {
          this.scrollToFirstError();
        });
        return;
      }

      // Prepare confirmation details
      let jobType = '-';
      if (this.formData.selectedSubCategory) {
        if (this.formData.selectedSubCategory.value === 'other') {
          jobType = `${this.$t('sendReport.otherPrefix')} (${this.formData.otherCategoryText})`;
        } else {
          jobType = this.formData.selectedSubCategory.label;
        }
      }
      let locationValue = '';
      if (this.formData.locationType === 'building') {
        let buildingDisplay = this.buildingSearchDetails || '-';

        // Use full text
        buildingDisplay = this.formData.roomName || this.buildingSearchDetails;

        if (this.selectedBuilding) {
          const label = getBuildingLocalizedName(this.selectedBuilding, this.$store.state.locale.lang);
          const room = this.formData.roomName;
          locationValue = room && room !== this.selectedBuilding.BUILDINGCODE ? `${label} /\n${room}` : label;
        } else if (buildingDisplay && buildingDisplay.includes(' / ')) {
          locationValue = buildingDisplay.replace(' / ', ' /\n');
        } else {
          locationValue = buildingDisplay;
        }
      } else {
        locationValue = this.$t('sendReport.outdoorLocated');
      }

      const summaryRow = (label, value, featured = false, extraClass = '') => `
        <div class="repair-confirm-row${featured ? ' repair-confirm-row--featured' : ''}${extraClass ? ' ' + extraClass : ''}">
          <span class="repair-confirm-label">${label}</span>
          <span class="repair-confirm-value">${this.escapeConfirmText(value)}</span>
        </div>`;

      const htmlContent = `
        <div class="repair-confirm-summary">
          ${summaryRow(this.$t('sendReport.confirmJobType'), jobType, true)}
          ${this.formData.assetCode ? summaryRow(this.$t('sendReport.assetCodeLabel'), this.formData.assetCode) : ''}
          ${summaryRow(this.$t('sendReport.confirmProblem'), this.formData.problemDetails)}
          ${summaryRow(this.$t('sendReport.confirmLocation'), locationValue)}
          <div class="repair-confirm-divider"></div>
          ${this.formData.onBehalfOf ? summaryRow(this.$t('sendReport.confirmOnBehalf'), this.formData.onBehalfOf) : ''}
          ${summaryRow(this.$t('sendReport.confirmPersonalPhone'), this.formData.personalPhone)}
          ${this.formData.telephoneWork ? summaryRow(this.$t('sendReport.confirmWorkPhone'), this.formData.telephoneWork) : ''}
          ${summaryRow(this.$t('sendReport.confirmAvailability'), this.formData.availableTime || '-', false, 'repair-confirm-row--availability')}
        </div>
      `;

      const confirmBtnText = this.$t('sendReport.confirm');
      const cancelBtnText = this.$t('sendReport.swalEdit');

      const result = await Swal.fire({
        title: this.$t('sendReport.swalConfirmTitle'),
        html: htmlContent,
        showCancelButton: true,
        confirmButtonText: confirmBtnText,
        cancelButtonText: cancelBtnText,
        reverseButtons: true,
        width: '460px',
        buttonsStyling: false,
        customClass: {
          popup: 'repair-confirm-popup',
          title: 'repair-confirm-title',
          htmlContainer: 'repair-confirm-content',
          actions: 'repair-confirm-actions',
          confirmButton: 'repair-confirm-button repair-confirm-button--confirm',
          cancelButton: 'repair-confirm-button repair-confirm-button--edit'
        }
      });

      if (result.isConfirmed) {
        this.submitTicket();
      }
    },

    async submitTicket() {
      this.isSubmitting = true;

      try {
        const uploadResult = await this.uploadImage();
        const imageName = uploadResult.filename;
        const imageNames = uploadResult.filenames;
        const sub = this.formData.selectedSubCategory;
        const isOther = sub.value === 'other';

        let response, result;

        if (isOther) {
          const ticketData = {
            userId: this.currentUser._id,
            onBehalfOf: this.formData.onBehalfOf,
            personalPhone: this.formData.personalPhone,
            telephoneWork: this.formData.telephoneWork || '-',
            otherCategoryText: this.formData.otherCategoryText,
            problemDetails: this.formData.problemDetails,
            assetCode: this.formData.assetCode ? this.formData.assetCode.trim() : undefined,
            availableTime: (this.formData.locationType === 'outdoor' && !this.formData.availableTime)
              ? '00:00 - 00:00'
              : this.formData.availableTime,
            locationType: this.formData.locationType,
            problemImage: imageName,
            problemImages: imageNames
          };

          if (this.formData.locationType === 'building') {
            if (this.formData.buildingId) {
              ticketData.buildingId = this.formData.buildingId;
            }
            ticketData.roomName = this.formData.roomName;
          } else {
            ticketData.latitude = this.formData.latitude;
            ticketData.longitude = this.formData.longitude;
          }

          response = await Service.ticket('post', ticketData, { urlAppend: '/other' });

        } else {
          // Normal Batch Ticket
          const ticketSubCategories = [{
            categoryId: sub.categoryId,
            name: sub.name || sub.value,
            subCategoryId: sub.subCategoryId
          }];

          const ticketData = {
            userId: this.currentUser._id,
            onBehalfOf: this.formData.onBehalfOf,
            personalPhone: this.formData.personalPhone,
            telephoneWork: this.formData.telephoneWork || '-',
            categoryType: 'predefined',
            subCategories: ticketSubCategories,
            problemDetails: this.formData.problemDetails,
            assetCode: this.formData.assetCode ? this.formData.assetCode.trim() : undefined,
            availableTime: (this.formData.locationType === 'outdoor' && !this.formData.availableTime)
              ? '00:00 - 00:00'
              : this.formData.availableTime,
            locationType: this.formData.locationType,
            problemImage: imageName,
            problemImages: imageNames
          };

          if (this.formData.locationType === 'building') {
            if (this.formData.buildingId) {
              ticketData.buildingId = this.formData.buildingId;
            }
            ticketData.roomName = this.formData.roomName;
          } else {
            ticketData.latitude = this.formData.latitude;
            ticketData.longitude = this.formData.longitude;
          }

          response = await Service.ticket('post', ticketData, { urlAppend: '/batch' });
        }

        result = response.data;

        if (result.success) {
          await Swal.fire({
            icon: "success",
            title: this.$t('sendReport.swalSuccessTitle'),
            confirmButtonColor: "#1e40af",
            timer: 2000,
            showConfirmButton: false
          });

          this.$emit('ticket-created', result.data);
          if (!this.isPageMode) {
            this.closeModal();
          }
        } else {
          throw new Error(result.message || 'Failed to create tickets');
        }

      } catch (error) {
        console.error('Submit error:', error);
        const responseData = error.response && error.response.data;
        const ratingRequired = responseData && (responseData.code === 'RATING_REQUIRED' || responseData.code === 'RATING_REQUIRED_GATEKEEPING');
        Swal.fire({
          icon: ratingRequired ? "warning" : "error",
          title: ratingRequired ? (this.$t('sendReport.ratingLockTitle') || this.$t('mainuser.evalBeforeNewTitle')) : this.$t('sendReport.swalErrorTitle'),
          text: ratingRequired ? (responseData.message || this.ratingLockMessage || this.$t('mainuser.evalBeforeNewText')) : ((responseData && responseData.message) || error.message || this.$t('sendReport.swalErrorText')),
          showCancelButton: ratingRequired,
          confirmButtonText: ratingRequired ? (this.$t('sendReport.goToRating') || "ไปที่แบบประเมิน") : (this.$t('login.ok') || "ตกลง"),
          cancelButtonText: this.$t('sendReport.cancel') || "ยกเลิก",
          confirmButtonColor: "#1e40af",
        }).then((result) => {
          if (ratingRequired && result && result.isConfirmed) {
            this.handleGoToRating();
          }
        });
      } finally {
        this.isSubmitting = false;
      }
    },

    handleGoToRating() {
      const ticket = this.firstUnratedTicket;
      const ticketId = ticket?._id || ticket?.id;
      if (!this.isPageMode) {
        this.closeModal();
      }
      if (this.$route?.path && this.$route.path.startsWith('/student-affairs')) {
        this.$router.push('/student-affairs/my-reports');
      } else {
        this.$router.push({ path: '/user/mainuser', query: { openTicketId: ticketId } });
      }
    },

    handleMobileNext() {
      this.errors = {};

      if (this.currentStep < 3) {
        if (this.validateStep(this.currentStep)) {
          this.currentStep += 1;
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          this.scrollToFirstError();
        }
        return;
      }

      this.sendReport();
    },

    handleMobileBack() {
      this.errors = {};
      if (this.currentStep > 1) {
        this.currentStep -= 1;
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      this.closeModal();
    },

    validateStep(step) {
      const errors = {};
      let isValid = true;

      if (step === 1) {
        if (!this.formData.selectedSubCategory) {
          errors.subCategories = this.$t('sendReport.errPickJobType');
          isValid = false;
        } else if (this.formData.selectedSubCategory.value === 'other') {
          if (!this.formData.otherCategoryText || this.formData.otherCategoryText.trim() === '') {
            errors.otherCategoryText = this.$t('sendReport.errSpecifyOther');
            isValid = false;
          }
        }
      }

      if (step === 2) {
        if (this.isEquipmentRelated && (!this.formData.assetCode || this.formData.assetCode.trim() === '')) {
          errors.assetCode = this.$t('sendReport.errAssetCode');
          isValid = false;
        }
        if (!this.formData.problemDetails || this.formData.problemDetails.trim() === '') {
          errors.problemDetails = this.$t('sendReport.errProblem');
          isValid = false;
        }
        if (this.imageError) {
          errors.problemImage = this.imageError;
          isValid = false;
        } else if (!this.repairImage) {
          errors.problemImage = this.$t('sendReport.errAttachImage');
          isValid = false;
        }
      }

      if (step === 3) {
        // Reuse final validation for the full form
        isValid = this.validateForm();
        this.errors = { ...this.errors, ...errors };
        return isValid;
      }

      this.errors = { ...this.errors, ...errors };
      return isValid;
    },

    closeModal() {
      if (this.isPageMode) {
        this.$emit('close');
        return;
      }
      this.localModal = false;
    },

    resetForm() {
      this.formData = {
        selectedBuildingObject: null, // Reset multiselect
        personalPhone: "",
        telephoneWork: "",
        onBehalfOf: "",
        categoryType: "predefined", // NEW
        selectedSubCategory: null, // CHANGED
        otherCategoryText: "", // NEW
        problemDetails: "",
        assetCode: "",
        availableTime: "",
        startTime: "",
        endTime: "",
        locationType: "",
        buildingId: "",
        roomName: "",
        latitude: null,
        longitude: null,
        problemImage: "",
      };

      // Restore last contact info from cache if available
      if (this.lastContactInfo) {
        if (this.lastContactInfo.personalPhone) {
          this.formData.personalPhone = this.lastContactInfo.personalPhone;
        }
        if (this.lastContactInfo.telephoneWork) {
          this.formData.telephoneWork = this.lastContactInfo.telephoneWork;
        }
        if (this.lastContactInfo.startTime && this.lastContactInfo.endTime) {
          this.formData.startTime = this.lastContactInfo.startTime;
          this.formData.endTime = this.lastContactInfo.endTime;
          this.updateAvailableTime();
        }
      }

      if (this.repairImagePreview) {
        URL.revokeObjectURL(this.repairImagePreview);
        this.repairImagePreview = "";
      }
      this.repairImage = null;
      this.outdoorLocation = {};
      this.errors = {};
      this.buildingSearchDetails = "";
      this.isBuildingSelected = false;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
      if (this.$refs.cameraInput) {
        this.$refs.cameraInput.value = "";
      }
      if (this.$refs.galleryInput) {
        this.$refs.galleryInput.value = "";
      }
    },

    isOptionPopular(option) {
      if (!option) return false;
      return this.popularOptionValues.has(option.value);
    },

    selectFromGuide(option) {
      this.formData.selectedSubCategory = option;
      this.showIconGuideModal = false;
    },

    openItemInfo(option) {
      if (!option) return;
      const isPop = this.isOptionPopular(option);
      const iconName = this.getJobIcon(option.label);
      Swal.fire({
        title: `<div style="font-size: 1.15rem; font-weight: 700; color: #1e40af;">${option.label}</div>`,
        html: `
          <div style="text-align: center; margin: 12px 0;">
            <div style="display: inline-flex; align-items: center; justify-content: center; width: 68px; height: 68px; border-radius: 16px; background: #eff6ff; color: #1e40af; font-size: 32px; margin-bottom: 12px; border: 1px solid #bfdbfe;">
              <i class="${iconName}"></i>
            </div>
            <div style="font-size: 0.95rem; color: #334155; margin-bottom: 6px;">
              <strong>หมวดหมู่งาน:</strong> ${option.categoryName || '-'}
            </div>
            ${option.slaHours ? `<div style="font-size: 0.85rem; color: #64748b; margin-bottom: 8px;"><strong>เวลามาตรฐาน (SLA):</strong> ภายใน ${option.slaHours} ชม.</div>` : ''}
            ${isPop ? `<div style="display: inline-block; background: #fee2e2; color: #ef4444; border: 1px solid #fecaca; border-radius: 9999px; padding: 2px 10px; font-size: 0.78rem; font-weight: 700;">🔥 ปัญหายอดนิยม (แจ้งบ่อย)</div>` : ''}
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: '✓ เลือกรายการนี้',
        cancelButtonText: 'ปิด',
        confirmButtonColor: '#1e40af',
        cancelButtonColor: '#94a3b8'
      }).then((result) => {
        if (result.isConfirmed) {
          this.formData.selectedSubCategory = option;
        }
      });
    },

    selectCategory(item) {
      if (!item || !item.underlyingOption) return;
      if (this.isCategoryActive(item)) {
        this.formData.selectedSubCategory = null;
      } else {
        this.formData.selectedSubCategory = item.underlyingOption;
      }
    },

    isCustomImageIcon(item) {
      if (!item || !item.icon || typeof item.icon !== 'string') return false;
      return item.icon.startsWith('/') || item.icon.startsWith('http://') || item.icon.startsWith('https://');
    },

    getCustomImageUrl(icon) {
      if (!icon) return '';
      if (icon.startsWith('http://') || icon.startsWith('https://')) return icon;
      return `${process.env.VUE_APP_APP_URL || ''}${icon}`;
    },

    handleIconImgError(item) {
      if (!item) return;
      const key = item.canonicalKey || item.value;
      this.$set(this.brokenCustomIcons, key, true);
    },

    getCategoryIcon(item) {
      if (!item) return 'cil-settings';
      if (item.icon && typeof item.icon === 'string' && /^(cil-|cib-|cif-)/.test(item.icon) && item.icon !== 'cil-wrench') {
        return item.icon;
      }
      return this.getJobIcon(item.label || item.name || item.rawLabel);
    },

    isCategoryActive(item) {
      return isCategoryActive(item, this.formData.selectedSubCategory);
    },

    getJobIcon(name) {
      if (!name) return 'cil-settings';
      const key = String(name).trim();
      const thaiName = JOB_TYPE_TRANSLATIONS[key]?.th || key;
      const n = (thaiName + ' ' + key).toLowerCase();

      // กล้องวงจรปิด / CCTV
      if (n.includes('กล้อง') || n.includes('cctv') || n.includes('camera')) return 'cil-camera';
      // เคเบิลทีวี / Cable TV
      if ((n.includes('cable') || n.includes('tv') || n.includes('เคเบิล') || n.includes('เคเบิ้ล') || n.includes('ทีวี') || n.includes('television')) && !n.includes('cctv')) return 'cil-tv';

      // คอมพิวเตอร์ / PC / จอภาพ
      if (n.includes('คอมพิวเตอร์') || n.includes('เครื่องคอม') || n.includes('computer') || n.includes('monitor') || n.includes('pc') || n.includes('laptop')) return 'cil-monitor';
      // อินเทอร์เน็ต / Wi-Fi
      if (n.includes('wi-fi') || n.includes('wifi') || n.includes('อินเทอร์เน็ต') || n.includes('เครือข่าย') || n.includes('internet') || n.includes('network') || n.includes('lan')) return 'cil-wifi-signal-1';
      // เครื่องพิมพ์ / สแกนเนอร์
      if (n.includes('เครื่องพิมพ์') || n.includes('สแกนเนอร์') || n.includes('ปริ้น') || n.includes('printer') || n.includes('scanner') || n.includes('print')) return 'cil-print';
      // แอร์ / ปรับอากาศ
      if (n.includes('ปรับอากาศ') || n.includes('เครื่องปรับอากาศ') || n.includes('ระบบเครื่องกล') || n.includes('น้ำเย็น') || n.includes('แอร์') || n.includes('air') || n.includes('cooling')) return 'cil-snowflake';
      // หลอดไฟ / แสงสว่าง
      if (n.includes('หลอดไฟ') || n.includes('แสงสว่าง') || n.includes('lighting') || n.includes('lamp') || n.includes('bulb') || n.includes('โคมไฟ')) return 'cil-lightbulb';
      // ปลั๊กไฟ / สวิตช์ / ไฟฟ้า / เบรกเกอร์
      if (n.includes('ปลั๊ก') || n.includes('สวิตช์') || n.includes('เต้ารับ') || n.includes('ไฟฟ้า') || n.includes('เบรกเกอร์') || n.includes('breaker') || n.includes('electric') || n.includes('power')) return 'cil-bolt';
      // ประปา / ท่อน้ำ / ก๊อกน้ำ / สุขาภิบาล
      if (n.includes('ท่อ') || n.includes('ก๊อก') || n.includes('น้ำรั่ว') || n.includes('น้ำไม่ไหล') || n.includes('น้ำไม่') || n.includes('ประปา') || n.includes('สุขาภิบาล') || n.includes('ชักโครก') || n.includes('plumb') || n.includes('sanitation') || n.includes('water') || n.includes('leak') || n.includes('drain') || n.includes('tap') || n.includes('toilet')) return 'cil-drop';
      // ประตู / หน้าต่าง / ลิฟต์ / ลูกบิด
      if (n.includes('ลิฟต์') || n.includes('ลิฟท์') || n.includes('ประตู') || n.includes('หน้าต่าง') || n.includes('ลูกบิด') || n.includes('กลอน') || n.includes('กุญแจ') || n.includes('door') || n.includes('elevator') || n.includes('window')) return 'cil-door';
      // อาคาร / โยธา / ผนัง / พื้น / เพดาน
      if (n.includes('อาคาร') || n.includes('โยธา') || n.includes('สถาปัตย์') || n.includes('ผนัง') || n.includes('พื้น') || n.includes('เพดาน') || n.includes('ร้าว') || n.includes('หลุดร่อน') || n.includes('กระเบื้อง') || n.includes('building') || n.includes('civil') || n.includes('wall') || n.includes('floor')) return 'cil-building';
      // โทรศัพท์
      if (n.includes('โทรศัพท์') || n.includes('phone') || n.includes('telephone')) return 'cil-phone';
      // เฟอร์นิเจอร์ / ครุภัณฑ์ / โต๊ะ / เก้าอี้
      if (n.includes('เฟอร์นิเจอร์') || n.includes('ครุภัณฑ์') || n.includes('วัสดุ') || n.includes('โต๊ะ') || n.includes('เก้าอี้') || n.includes('furniture') || n.includes('couch') || n.includes('chair') || n.includes('desk')) return 'cil-couch';
      // ความสะอาด / สิ่งแวดล้อม / ขยะ
      if (n.includes('ความสะอาด') || n.includes('สิ่งแวดล้อม') || n.includes('ขยะ') || n.includes('trash') || n.includes('clean') || n.includes('environment')) return 'cil-trash';
      // ความปลอดภัย / อัคคีภัย
      if (n.includes('ความปลอดภัย') || n.includes('อัคคีภัย') || n.includes('ดับเพลิง') || n.includes('safety') || n.includes('fire') || n.includes('lock')) return 'cil-lock-locked';
      // โสต / ภาพ / เสียง
      if (n.includes('โสต') || n.includes('ภาพ') || n.includes('เสียง') || n.includes('audio') || n.includes('sound') || n.includes('mic')) return 'cil-audio';
      // ห้องเรียน / โปรเจกเตอร์
      if (n.includes('ห้องเรียน') || n.includes('โปรเจกเตอร์') || n.includes('การสอนออนไลน์') || n.includes('projector')) return 'cil-screen-desktop';
      // บัญชี / อีเมล / google
      if (n.includes('บัญชีผู้ใช้') || n.includes('อีเมล') || n.includes('google') || n.includes('email') || n.includes('account')) return 'cil-at';
      // ระบบสารสนเทศ / ซอฟต์แวร์
      if (n.includes('ระบบสารสนเทศ') || n.includes('ซอฟต์แวร์') || n.includes('software') || n.includes('system') || n.includes('code')) return 'cil-code';

      // อื่น ๆ
      return 'cil-settings';
    },
  },
};
</script>

<style scoped>

.body-font {
  font-family: "Sarabun", sans-serif;
  background: white;
}

.send-report-page {
  background: #ffffff;
  padding: 24px;
  border-radius: 20px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.08);
  min-height: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;
}

.page-step-label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.08);
  color: #1e40af;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  margin-bottom: 8px;
}

.page-title {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  color: #1f1f1f;
}

.page-subtitle {
  margin-top: 8px;
  color: #6b7280;
  max-width: 680px;
}

.page-close-btn {
  border: none;
  background: transparent;
  color: #6b7280;
  font-size: 28px;
  cursor: pointer;
  padding: 8px;
}

.page-close-btn.admin-close-btn {
  font-size: 0.88rem;
  font-weight: 600;
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  background-color: #ffffff;
  color: #4f5d73;
  padding: 6px 12px;
  display: inline-flex;
  align-items: center;
  transition: all 0.2s ease;
}
.page-close-btn.admin-close-btn:hover {
  background-color: #f8f9fa;
  border-color: #c4c9d0;
  color: #2c384a;
}

.step-progress {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 24px;
}

.step-indicator {
  background: #f2f4f7;
  color: #6b7280;
  border-radius: 999px;
  padding: 12px 0;
  text-align: center;
  font-weight: 700;
  transition: all 0.25s ease;
}

.step-indicator.active {
  background: #1e40af;
  color: #ffffff;
}

.page-form {
  display: grid;
  gap: 24px;
}

.page-actions {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 16px;
}

.page-actions-desktop {
  justify-content: flex-end;
}

@media (min-width: 769px) {
  .send-report-page {
    width: 100%;
    max-width: none;
    margin: 0;
    padding: 20px;
    border: 1px solid rgba(37, 99, 235, 0.1);
    background: #ffffff;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
  }

  .page-form {
    gap: 18px;
  }

  .wizard-progress-block {
    margin: 0 0 16px;
    padding: 14px 18px;
    border: 1px solid rgba(37, 99, 235, 0.1);
    border-radius: 16px;
    background: #fff;
  }

  .wizard-progress-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .wizard-progress-heading h3 {
    margin: 0;
    color: #1e40af;
    font-size: 1.2rem;
    font-weight: 800;
  }

  .step-progress {
    display: flex;
    gap: 0;
    height: 8px;
    margin: 0;
    overflow: hidden;
    border-radius: 999px;
    background: #e9e1e1;
  }

  .step-indicator {
    flex: 1;
    height: 8px;
    padding: 0;
    border-radius: 0;
    background: transparent;
  }

  .step-indicator.active {
    background: #1e40af;
  }

  .step-indicator span {
    display: none;
  }

  .page-form>div:not(.page-actions) {
    padding: 18px;
    border: 1px solid rgba(37, 99, 235, 0.1);
    border-radius: 18px;
    background: rgba(255, 255, 255, 0.92);
    box-shadow: 0 10px 28px rgba(37, 99, 235, 0.05);
  }

  .page-form h5 {
    color: #1e40af;
    font-size: 1.08rem;
  }

  .job-type-grid {
    grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
    gap: 12px;
  }

  .job-card {
    width: 74px;
    height: 74px;
    border: 1px solid rgba(37, 99, 235, 0.16);
    border-radius: 14px;
    background: #f8fafc;
  }

  .job-card.active {
    color: #fff;
    background: #1e40af;
    border-color: #1e40af;
    box-shadow: 0 9px 20px rgba(37, 99, 235, 0.18);
  }

  .area-card {
    min-height: 120px;
    border: 1px solid rgba(37, 99, 235, 0.14);
    border-radius: 16px;
    background: #f8fafc;
  }

  .sub-box {
    padding: 20px;
    border-color: rgba(37, 99, 235, 0.1);
    border-radius: 14px;
    background: #f8fafc;
    box-shadow: none;
  }

  .form-control,
  ::v-deep .form-control {
    min-height: 46px;
    border-color: rgba(37, 99, 235, 0.2);
    border-radius: 10px;
  }

  .upload-button {
    border: 2px dashed rgba(37, 99, 235, 0.24);
    background: #f8fafc;
  }

  .page-actions-desktop {
    position: sticky;
    bottom: 18px;
    z-index: 20;
    margin: 4px 0 0;
    padding: 14px;
    border: 1px solid rgba(37, 99, 235, 0.1);
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.94);
    box-shadow: 0 12px 34px rgba(37, 99, 235, 0.12);
    backdrop-filter: blur(10px);
  }

  .page-actions-desktop .btn {
    flex: 0 0 auto !important;
    min-width: 180px;
    min-height: 46px;
    border-radius: 999px;
  }

  .page-actions-desktop .btn-success {
    border-color: #1e40af;
    background: #1e40af;
  }

  .page-actions:not(.page-actions-desktop) {
    justify-content: flex-end;
    margin-top: 0;
    padding: 16px 0 0;
    border-top: 1px solid rgba(37, 99, 235, 0.1);
  }

  .page-actions:not(.page-actions-desktop) .btn {
    flex: 0 0 auto !important;
    min-width: 180px;
    min-height: 46px;
    margin: 0 !important;
    border-radius: 999px;
  }

  .page-actions:not(.page-actions-desktop) .wizard-next-btn {
    background: #1e40af !important;
    border-color: #1e40af !important;
  }
}

.reporter-details-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 18px;
}

.reporter-field {
  min-width: 0;
}

.reporter-name-text {
  min-height: 46px;
  padding: 10px 0;
  color: #2f343a;
  font-size: 1rem;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.reporter-field--schedule {
  grid-column: 1 / -1;
  padding-top: 2px;
}

@media (min-width: 769px) {
  .reporter-field--schedule .date-time-container {
    display: grid;
    grid-template-columns: minmax(220px, 1.15fr) minmax(360px, 1.85fr);
    align-items: end;
    gap: 14px;
  }

  .reporter-field--schedule .date-time-container>.mb-2 {
    margin-bottom: 0 !important;
  }

  .reporter-field--schedule .time-range-container {
    margin-top: 0;
  }
}

@media (min-width: 769px) and (max-width: 1100px) {
  .send-report-page {
    padding: 16px;
    border-radius: 16px;
  }

  .wizard-progress-block,
  .page-form>div:not(.page-actions) {
    padding: 15px;
    border-radius: 14px;
  }

  .reporter-field--schedule .date-time-container {
    grid-template-columns: 1fr;
    gap: 10px;
  }
}

@media (max-width: 768px) {

  /* iOS Safari zooms focused form controls whose computed font size is below
     16px. Keep controls at 16px while preserving user pinch-to-zoom. */
  .send-report-page input,
  .send-report-page textarea,
  .send-report-page select,
  .send-report-page .form-control,
  ::v-deep .send-report-page input,
  ::v-deep .send-report-page textarea,
  ::v-deep .send-report-page select,
  ::v-deep .send-report-page .multiselect__input,
  ::v-deep .send-report-page .multiselect__single {
    font-size: 16px !important;
  }

  .send-report-page {
    padding: 16px 12px 32px;
    min-height: auto;
  }

  .page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .page-actions {
    flex-direction: column;
  }

  .page-actions .c-button,
  .page-actions .btn,
  .page-actions .button {
    width: 100%;
  }
}

.lbl {
  font-weight: 600;
  color: #495057;
  display: block;
  margin-bottom: 4px;
}

.text-danger {
  color: #e55353;
}

.is-invalid-multiselect>>>.multiselect__tags {
  border-color: #e55353 !important;
}

.icon-box {
  min-width: 48px;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.bg-gradient-primary {
  background: #1e40af !important;
}

.bg-gradient-warning {
  background: #1e40af !important;
}

.border-dashed {
  border-top: 1px dashed #ccc;
}



.area-grid {
  display: flex;
  gap: 10px;
}

.category-type-selection {
  display: flex;
  gap: 10px;
}

.category-type-card {
  flex: 1;
  background: #fff;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 15px;
  text-align: center;
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
  height: 90px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 0;
}

.category-type-card:hover {
  border-color: #bdc3c7;
  transform: translateY(-2px);
}

.category-option-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.clickable-label {
  cursor: pointer;
  transition: opacity 0.2s;
}

.clickable-label:hover {
  opacity: 0.8;
}

.category-type-card.active {
  border-color: #2eb85c;
  background: #f0fff4;
  color: #2eb85c;
  box-shadow: 0 6px 12px rgba(46, 184, 92, 0.15);
}

.category-type-card .check {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #e9ecef;
  color: transparent;
  font-size: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.category-type-card.active .check {
  background: #2eb85c;
  border-color: #2eb85c;
  color: #fff;
}

.area-card {
  flex: 1;
  background: #fff;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 15px;
  text-align: center;
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
  height: 110px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 0;
}

.area-card:hover {
  border-color: #bdc3c7;
  transform: translateY(-2px);
}

.area-card.active {
  border-color: #1e40af;
  background: #f0f9ff;
  color: #1e40af;
  box-shadow: 0 6px 12px rgba(37, 99, 235, 0.15);
}

.area-card.active .area-card-icon {
  color: currentColor;
}

.check {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #e9ecef;
  color: transparent;
  font-size: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.area-card.active .check {
  background: #1e40af;
  border-color: #1e40af;
  color: #fff;
}

.sub-box {
  background: #fff;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #e2e6ea;
  margin-top: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.custom-file-upload-wrapper {
  display: block;
}

.upload-button {
  flex-grow: 1;
  background: #fff;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 15px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  height: 110px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #1e40af;
  font-weight: 600;
  font-size: 1rem;
}

.upload-button:hover {
  border-color: #1e40af;
  transform: translateY(-2px);
}

.upload-button:focus,
.upload-button:focus-visible,
.upload-button:active {
  outline: none;
  border-color: #1e40af;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.upload-button.border-danger {
  border-color: #e55353;
}

.upload-icon-theme,
.area-card-icon {
  color: #1e40af;
}

.upload-icon-theme {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.remove-image-button {
  height: 110px;
  padding: 0 15px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.9rem;
}

.remove-image-button i {
  font-size: 1.5rem;
  margin-bottom: 5px;
}

.invalid-feedback {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

/* Time Range Picker Styles */
.time-range-container {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.time-input-group {
  display: flex;
  align-items: center;
  gap: 5px;
}

.time-picker-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.time-picker-wrapper.disabled {
  opacity: 0.65;
  pointer-events: none;
}

.time-picker-icon {
  position: absolute;
  left: 10px;
  z-index: 2;
  color: #1e40af;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.time-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #495057;
  margin: 0;
  min-width: 35px;
}

.time-input {
  width: 135px;
  font-size: 0.875rem;
  padding: 0.375rem 0.5rem 0.375rem 32px !important;
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  background-color: #fff;
  cursor: pointer;
}

.time-input:focus {
  border-color: #1e40af;
  outline: 0;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.time-input.is-invalid {
  border-color: #dc3545;
}

.time-input::-webkit-calendar-picker-indicator {
  cursor: pointer;
  filter: invert(0.5);
}

.time-input::-webkit-calendar-picker-indicator:hover {
  filter: invert(0.3);
}

.time-separator {
  font-weight: bold;
  color: #6c757d;
  margin: 0 5px;
}

@media (max-width: 576px) {
  .time-range-container {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .time-input-group {
    justify-content: space-between;
  }

  .time-input {
    width: auto;
    flex: 1;
  }

  .time-separator {
    text-align: center;
    margin: 0;
  }
}

/* Custom Multiselect Aesthetic */
::v-deep .multiselect__tags {
  border: 1px solid #ced4da;
  border-radius: 6px;
  min-height: 45px;
  /* Matches other inputs */
  padding-top: 8px;
}

::v-deep .multiselect__tag {
  background: #1e40af;
  color: #fff;
  border-radius: 4px;
  margin-bottom: 0;
  margin-top: 2px;
}

::v-deep .multiselect__tag-icon:after {
  color: #d1fae5;
}

::v-deep .multiselect__tag-icon:hover {
  background: #1e3a8a;
}

::v-deep .multiselect__option--highlight {
  background: #1e40af;
  background: #1e40af;
}

::v-deep .multiselect__option--highlight:after {
  background: transparent;
}

/* Slow animation for SweetAlert2 */
::v-deep .swal2-show-slow {
  animation: swal2-show-slow 0.6s ease-out !important;
}

::v-deep .swal2-hide-slow {
  animation: swal2-hide-slow 0.5s ease-out forwards !important;
}

@keyframes swal2-show-slow {
  0% {
    transform: scale(0.7);
    opacity: 0;
  }

  50% {
    transform: scale(1.05);
  }

  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes swal2-hide-slow {
  0% {
    transform: scale(1);
    opacity: 1;
  }

  100% {
    transform: scale(0.5);
    opacity: 0;
  }
}
</style>

<style>
.send-report-page .request-cancel-btn,
.send-report-page .request-confirm-btn,
.modal-content .request-cancel-btn,
.modal-content .request-confirm-btn {
  flex: 1 1 0 !important;
  width: 100% !important;
  min-width: 0 !important;
  min-height: 52px !important;
  margin: 0 !important;
  padding: 10px 18px !important;
  border-radius: 8px !important;
  font-size: 1rem !important;
  font-weight: 800 !important;
  box-shadow: none !important;
}

.send-report-page .request-cancel-btn,
.modal-content .request-cancel-btn {
  color: #64748b !important;
  border: 1px solid #cbd5e1 !important;
  background: #fff !important;
}

.send-report-page .request-cancel-btn:hover,
.send-report-page .request-cancel-btn:focus,
.modal-content .request-cancel-btn:hover,
.modal-content .request-cancel-btn:focus {
  color: #334155 !important;
  border-color: #94a3b8 !important;
  background: #f1f5f9 !important;
}

.send-report-page .request-confirm-btn,
.modal-content .request-confirm-btn {
  color: #fff !important;
  border: 1px solid #1e40af !important;
  background: #1e40af !important;
}

.send-report-page .request-confirm-btn:hover,
.send-report-page .request-confirm-btn:focus,
.modal-content .request-confirm-btn:hover,
.modal-content .request-confirm-btn:focus {
  border-color: #1e3a8a !important;
  background: #1e3a8a !important;
}

@media (min-width: 769px) {
  .send-report-page .page-actions .request-cancel-btn,
  .send-report-page .page-actions .request-confirm-btn {
    flex: 0 0 220px !important;
    width: 220px !important;
  }
}

/* iPad portrait: keep the mobile flow but prevent cards from stretching too wide. */
@media (min-width: 641px) and (max-width: 768px) {
  .send-report-page {
    padding-right: 22px;
    padding-left: 22px;
  }

  .send-report-page .job-type-grid,
  .wizard-step-one .job-type-grid {
    grid-template-columns: repeat(auto-fit, minmax(124px, 156px));
    justify-content: start;
    gap: 12px;
  }

  .send-report-page .job-card,
  .wizard-step-one .job-card {
    height: 96px;
  }

  .wizard-step-one .page-actions {
    max-width: 520px;
    margin-top: 24px;
    margin-left: auto;
    margin-right: 0;
  }
}

/* ==========================================================================
   Repair Request Confirmation Dialog (Unified across all roles)
   ========================================================================== */
body .swal2-container .swal2-popup.repair-confirm-popup,
body.user-unified-rounded-ui .swal2-container .swal2-popup.repair-confirm-popup,
body.role-unified-theme .swal2-container .swal2-popup.repair-confirm-popup,
.repair-confirm-popup {
  width: min(460px, calc(100vw - 28px)) !important;
  padding: 0 !important;
  overflow: hidden !important;
  border: 1px solid rgba(37, 99, 235, 0.13) !important;
  border-radius: 22px !important;
  background: #fffdfd !important;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.18) !important;
}

body .swal2-container .swal2-title.repair-confirm-title,
body.user-unified-rounded-ui .swal2-container .swal2-title.repair-confirm-title,
body.role-unified-theme .swal2-container .swal2-title.repair-confirm-title,
.repair-confirm-title {
  display: block !important;
  margin: 0 !important;
  padding: 24px 24px 18px !important;
  color: #1e40af !important;
  font-size: 1.45rem !important;
  font-weight: 800 !important;
  line-height: 1.35 !important;
  text-align: left !important;
  background: #f8fafc !important;
  border-bottom: 1px solid rgba(37, 99, 235, 0.1) !important;
}

body .swal2-container .swal2-html-container.repair-confirm-content,
body.user-unified-rounded-ui .swal2-container .swal2-html-container.repair-confirm-content,
body.role-unified-theme .swal2-container .swal2-html-container.repair-confirm-content,
.repair-confirm-content {
  margin: 0 !important;
  padding: 18px 24px 8px !important;
  overflow-x: hidden !important;
  text-align: left !important;
}

.repair-confirm-summary {
  text-align: left;
}

.repair-confirm-row {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: 14px;
  align-items: start;
  padding: 9px 0;
}

.repair-confirm-label {
  color: #695d5d;
  font-size: 0.88rem;
  font-weight: 700;
}

.repair-confirm-value {
  color: #3d3030;
  font-size: 0.92rem;
  font-weight: 600;
  line-height: 1.5;
  text-align: right;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: pre-line;
}

.repair-confirm-row--availability .repair-confirm-value {
  white-space: nowrap;
  font-size: 0.85rem;
  font-weight: 700;
}

.repair-confirm-row--featured .repair-confirm-value {
  color: #1e40af;
  font-weight: 800;
}

.repair-confirm-divider {
  height: 1px;
  margin: 7px 0;
  background: rgba(37, 99, 235, 0.1);
}

body .swal2-container .swal2-actions.repair-confirm-actions,
body.user-unified-rounded-ui .swal2-container .swal2-actions.repair-confirm-actions,
body.user-unified-rounded-ui .swal2-actions.repair-confirm-actions,
body.role-unified-theme .swal2-container .swal2-actions.repair-confirm-actions,
body .swal2-actions.repair-confirm-actions,
.repair-confirm-actions {
  display: grid !important;
  grid-template-columns: 1fr 1.25fr !important;
  gap: 12px !important;
  width: 100% !important;
  margin: 10px 0 0 !important;
  padding: 16px 24px 22px !important;
  background: #f8fafc !important;
  border: 0 !important;
  border-top: 1px solid rgba(37, 99, 235, 0.08) !important;
  box-sizing: border-box !important;
}

body .swal2-container .repair-confirm-button,
body.user-unified-rounded-ui .swal2-container .repair-confirm-button,
body.user-unified-rounded-ui .repair-confirm-button,
body.role-unified-theme .swal2-container .repair-confirm-button,
.repair-confirm-button {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 100% !important;
  min-height: 46px !important;
  margin: 0 !important;
  padding: 10px 18px !important;
  border-radius: 12px !important;
  font-size: 0.95rem !important;
  font-weight: 700 !important;
  cursor: pointer !important;
  box-sizing: border-box !important;
  transition: all 0.2s ease !important;
}

body .swal2-container .repair-confirm-button--edit,
body.user-unified-rounded-ui .swal2-container .repair-confirm-button--edit,
body.user-unified-rounded-ui .swal2-actions .swal2-cancel.repair-confirm-button--edit,
body.role-unified-theme .swal2-container .repair-confirm-button--edit,
.repair-confirm-actions .repair-confirm-button--edit,
.repair-confirm-actions .swal2-cancel {
  grid-column: 1 !important;
  order: 1 !important;
  border: 1px solid #1e40af !important;
  background: #fff !important;
  color: #1e40af !important;
  box-shadow: none !important;
}

body .swal2-container .repair-confirm-button--edit:hover,
body.user-unified-rounded-ui .swal2-container .repair-confirm-button--edit:hover,
body.user-unified-rounded-ui .swal2-actions .swal2-cancel.repair-confirm-button--edit:hover,
body.role-unified-theme .swal2-container .repair-confirm-button--edit:hover,
.repair-confirm-actions .repair-confirm-button--edit:hover,
.repair-confirm-actions .swal2-cancel:hover {
  background: #f1f5f9 !important;
  color: #1e3a8a !important;
  border-color: #1e3a8a !important;
  transform: none !important;
  box-shadow: none !important;
}

body .swal2-container .repair-confirm-button--confirm,
body.user-unified-rounded-ui .swal2-container .repair-confirm-button--confirm,
body.user-unified-rounded-ui .swal2-actions .swal2-confirm.repair-confirm-button--confirm,
body.role-unified-theme .swal2-container .repair-confirm-button--confirm,
.repair-confirm-actions .repair-confirm-button--confirm,
.repair-confirm-actions .swal2-confirm {
  grid-column: 2 !important;
  order: 2 !important;
  border: 1px solid #1e40af !important;
  background: #1e40af !important;
  color: #fff !important;
  box-shadow: none !important;
}

body .swal2-container .repair-confirm-button--confirm:hover,
body.user-unified-rounded-ui .swal2-container .repair-confirm-button--confirm:hover,
body.user-unified-rounded-ui .swal2-actions .swal2-confirm.repair-confirm-button--confirm:hover,
body.role-unified-theme .swal2-container .repair-confirm-button--confirm:hover,
.repair-confirm-actions .repair-confirm-button--confirm:hover,
.repair-confirm-actions .swal2-confirm:hover {
  background: #1e3a8a !important;
  border-color: #1e3a8a !important;
  color: #fff !important;
  transform: none !important;
  box-shadow: none !important;
}

@media (max-width: 576px) {
  body .swal2-container .swal2-popup.repair-confirm-popup,
  body.user-unified-rounded-ui .swal2-container .swal2-popup.repair-confirm-popup,
  body.role-unified-theme .swal2-container .swal2-popup.repair-confirm-popup,
  .repair-confirm-popup {
    border-radius: 18px !important;
  }

  body .swal2-container .swal2-title.repair-confirm-title,
  body.user-unified-rounded-ui .swal2-container .swal2-title.repair-confirm-title,
  body.role-unified-theme .swal2-container .swal2-title.repair-confirm-title,
  .repair-confirm-title {
    padding: 20px 18px 15px !important;
    font-size: 1.2rem !important;
  }

  body .swal2-container .swal2-html-container.repair-confirm-content,
  body.user-unified-rounded-ui .swal2-container .swal2-html-container.repair-confirm-content,
  body.role-unified-theme .swal2-container .swal2-html-container.repair-confirm-content,
  .repair-confirm-content {
    padding: 12px 18px 4px !important;
  }

  .repair-confirm-row {
    grid-template-columns: max-content minmax(0, 1fr);
    gap: 10px;
    padding: 7px 0;
  }

  .repair-confirm-label,
  .repair-confirm-value {
    font-size: 0.82rem;
  }

  .repair-confirm-row--availability .repair-confirm-value {
    font-size: 0.78rem;
    white-space: nowrap;
  }

  body .swal2-container .swal2-actions.repair-confirm-actions,
  body.user-unified-rounded-ui .swal2-container .swal2-actions.repair-confirm-actions,
  body.user-unified-rounded-ui .swal2-actions.repair-confirm-actions,
  body.role-unified-theme .swal2-container .swal2-actions.repair-confirm-actions,
  body .swal2-actions.repair-confirm-actions,
  .repair-confirm-actions {
    padding: 14px 18px 18px !important;
    gap: 10px !important;
  }
}
</style>

<style scoped>
/* Job Type Grid */
.repair-category-groups {
  display: grid;
  gap: 18px;
}

.repair-category-group {
  padding: 14px;
  border: 1px solid rgba(37, 99, 235, 0.14);
  border-radius: 12px;
  background: #f8fafc;
}

.repair-category-title {
  margin: 0 0 12px;
  color: #1e40af;
  font-size: 0.95rem;
  font-weight: 700;
}

.job-type-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(75px, 1fr));
  gap: 8px;
}

.job-item-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
}

.job-item-wrapper:hover .job-card {
  border-color: #bdc3c7;
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.job-card {
  background: #fff;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 6px;
  text-align: center;
  /* cursor: pointer; Valid on wrapper now */
  position: relative;
  transition: all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
  height: 60px;
  /* Revert to smaller size since text is gone */
  width: 60px;
  /* Make it square */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
}

/* Hover effect is now on .job-item-wrapper */

.job-card.active {
  border-color: #1e40af;
  background: #f0f9ff;
  color: #1e40af;
  box-shadow: 0 6px 12px rgba(37, 99, 235, 0.15);
}

.job-card-icon {
  display: inline-flex;
  min-height: 32px;
  align-items: center;
  justify-content: center;
  color: #1e40af;
}

.job-card.active .job-card-icon {
  color: currentColor;
}

.job-card-label {
  display: block;
  width: 100%;
  overflow: hidden;
  color: #5f4d4d;
  font-size: 0.74rem;
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .reporter-details-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .reporter-field--schedule {
    grid-column: auto;
  }

  .send-report-page {
    padding: 0 16px 28px;
    border-radius: 0;
    box-shadow: none;
    min-height: calc(100vh - 150px);
  }

  .wizard-progress-block {
    margin-bottom: 28px;
  }

  .wizard-progress-heading {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
  }

  .wizard-progress-heading h3 {
    margin: 0;
    color: #1e40af;
    font-size: 1.15rem;
    font-weight: 700;
  }

  .wizard-progress-heading span {
    flex: 0 0 auto;
    color: #64748b;
    font-size: 0.8rem;
    font-weight: 600;
  }

  .step-progress {
    display: flex;
    gap: 0;
    height: 8px;
    margin: 0;
    overflow: hidden;
    border-radius: 999px;
    background: #e8e3e3;
  }

  .step-indicator {
    flex: 1 1 33.333%;
    min-width: 0;
    height: 8px;
    padding: 0;
    border-radius: 0;
    background: transparent;
  }

  .step-indicator.active {
    background: #1e40af;
  }

  .step-indicator span {
    display: none;
  }

  .page-form {
    display: block;
  }

  .page-form>div:not(.page-actions) {
    margin-bottom: 30px;
  }

  .page-form .icon-box {
    display: none;
  }

  .page-form .d-flex.align-items-center.mb-4 {
    padding-bottom: 10px;
    margin-bottom: 18px !important;
    border-bottom: 1px solid #e2e8f0;
  }

  .page-form h5 {
    color: #1e40af;
  }

  .job-type-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }

  .job-card {
    width: 100%;
    height: 82px;
    padding: 8px 4px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    color: #334155;
    gap: 5px;
  }

  .job-card.active {
    color: #fff;
    background: #1e40af;
    border-color: #1e40af;
  }

  .job-card.active .job-card-label {
    color: #fff;
  }

  .job-card-label {
    font-size: 0.7rem;
  }

  .area-grid {
    flex-direction: column;
    gap: 8px;
  }

  .area-card {
    width: 100%;
    height: auto;
    min-height: 48px;
    padding: 11px 14px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
  }

  .area-card>div:first-of-type,
  .area-card small,
  .area-card .check {
    display: none;
  }

  .area-card.active {
    color: #fff;
    background: #1e40af;
    border-color: #1e40af;
    box-shadow: none;
  }

  .sub-box {
    padding: 0;
    border: 0;
    box-shadow: none;
  }

  .form-control,
  ::v-deep .form-control {
    min-height: 48px;
    border-color: #cbd5e1;
    border-radius: 6px;
  }

  textarea.form-control {
    min-height: 130px;
  }

  .upload-button {
    width: 100%;
    min-height: 104px;
    border: 2px dashed rgba(37, 99, 235, 0.28);
    border-radius: 6px;
  }

  .page-actions {
    position: sticky;
    bottom: 0;
    z-index: 20;
    flex-direction: row;
    flex-wrap: nowrap;
    gap: 12px;
    margin: 30px -16px -28px;
    padding: 16px;
    background: rgba(255, 255, 255, 0.96);
    border-top: 1px solid #e2e8f0;
    box-shadow: 0 -8px 24px rgba(37, 99, 235, 0.06);
  }

  .page-actions.page-actions-desktop {
    margin-top: 0;
  }

  .page-actions .btn {
    width: auto;
    min-height: 46px;
    margin: 0 !important;
    border-radius: 12px;
  }

  .wizard-back-btn {
    flex: 0 0 32% !important;
    color: #1e40af;
    border-color: #1e40af;
  }

  .wizard-next-btn {
    background: #1e40af;
    border-color: #1e40af;
    box-shadow: 0 8px 18px rgba(37, 99, 235, 0.18);
  }
}

.job-card .check {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #1e40af;
  color: white;
  font-size: 10px;
  font-weight: bold;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.2);
}

.date-time-container {
  display: flex;
  flex-direction: column;
}

.job-label {
  font-size: 0.7rem;
  font-weight: 600;
  line-height: 1.2;
  word-break: break-word;
  white-space: normal;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  /* Show max 2 lines */
  line-clamp: 2;
  -webkit-box-orient: vertical;
  color: #555;
}

.small-text {
  font-size: 10px;
}

@media (min-width: 769px) {
  .send-report-page .job-type-grid {
    grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
    gap: 12px;
  }

  .send-report-page .job-card {
    width: 108px;
    height: 96px;
    border: 1px solid rgba(37, 99, 235, 0.16);
    border-radius: 14px;
    background: #f8fafc;
  }

  .send-report-page .job-card.active {
    color: #fff;
    background: #1e40af;
    border-color: #1e40af;
  }

  .send-report-page .job-card.active .job-card-label {
    color: #fff;
  }
}

@media (max-width: 768px) {
  .send-report-page.wizard-step-one {
    min-height: 0;
  }

  .wizard-step-one .wizard-progress-block {
    margin-bottom: 18px;
  }

  .wizard-step-one .page-form {
    display: flex;
    flex-direction: column;
  }

  .wizard-step-one .page-form>div:first-child {
    min-height: 0;
  }

  .wizard-step-one .page-form>div:first-child>.d-flex {
    margin-bottom: 10px !important;
  }

  .wizard-step-one .job-type-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }

  .wizard-step-one .job-card {
    height: 82px;
  }

  .wizard-step-one .job-card-label {
    font-size: 0.7rem;
  }

  .wizard-step-one .page-actions {
    position: static;
    flex: 0 0 auto;
    margin: 20px 0 0;
    padding: 0;
    border: 0;
    background: transparent;
    box-shadow: none;
  }
}

@media (min-width: 641px) and (max-width: 768px) {
  .send-report-page {
    padding-right: 22px;
    padding-left: 22px;
  }

  .send-report-page .job-type-grid,
  .wizard-step-one .job-type-grid {
    grid-template-columns: repeat(auto-fit, minmax(124px, 156px));
    justify-content: start;
    gap: 12px;
  }

  .send-report-page .job-card,
  .wizard-step-one .job-card {
    height: 96px;
  }

  .wizard-step-one .page-actions {
    max-width: 520px;
    margin-top: 24px;
    margin-right: 0;
    margin-left: auto;
  }
}

/* Long taxonomy labels must remain readable instead of overflowing or being
   replaced with an ellipsis. Keep every card in a row the same useful size. */
.send-report-page .job-type-grid,
.repair-category-group .job-type-grid {
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  align-items: stretch;
}

.send-report-page .job-item-wrapper,
.repair-category-group .job-item-wrapper {
  min-width: 0;
  height: 100%;
}

.send-report-page .job-card,
.repair-category-group .job-card {
  width: 100%;
  height: 100%;
  min-height: 118px;
  padding: 12px 10px;
  gap: 8px;
}

.send-report-page .job-card-label,
.repair-category-group .job-card-label {
  display: block;
  overflow: visible;
  white-space: normal;
  text-overflow: clip;
  overflow-wrap: anywhere;
  word-break: normal;
  line-height: 1.35;
}

@media (max-width: 640px) {

  .send-report-page .job-type-grid,
  .repair-category-group .job-type-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .send-report-page .job-card,
  .repair-category-group .job-card {
    min-height: 126px;
  }
}

@media (min-width: 641px) and (max-width: 768px) {

  .send-report-page .job-type-grid,
  .repair-category-group .job-type-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

/* Uploaded Image Preview Card */
.uploaded-image-preview-wrapper {
  width: 100%;
}

.uploaded-image-card {
  background: #ffffff;
  border: 2px solid #1e40af;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(37, 99, 235, 0.08);
  transition: all 0.2s ease;
}

.preview-img-container {
  width: 100%;
  max-height: 240px;
  min-height: 140px;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.uploaded-preview-img {
  width: 100%;
  max-height: 240px;
  object-fit: contain;
  display: block;
}

.preview-card-footer {
  padding: 10px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background: #ffffff;
  border-top: 1px solid #eeeeee;
}

.preview-file-title {
  font-size: 13px;
  font-weight: 600;
  color: #333333;
  min-width: 0;
  display: flex;
  align-items: center;
}

.preview-btn-group {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.btn-action-change,
.btn-action-remove {
  border-radius: 6px;
  font-weight: 600;
  font-size: 12px;
  padding: 4px 10px;
  display: inline-flex;
  align-items: center;
}

/* Date picker wrapper: แสดง DD/MM/YYYY แทน browser locale */
.date-display-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.date-display-wrapper:focus-within {
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}
.date-display-wrapper.is-invalid {
  border-color: #dc3545;
}
.date-display-wrapper.is-invalid:focus-within {
  box-shadow: 0 0 0 0.25rem rgba(220, 53, 69, 0.25);
}
.date-display-input {
  flex: 1;
  width: 100%;
  border: none;
  outline: none;
  padding: 0.375rem 2.5rem 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  background: transparent;
  cursor: pointer;
  color: #212529;
}
.date-display-input::placeholder {
  color: #6c757d;
}
.date-picker-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  pointer-events: none;
  display: flex;
  align-items: center;
}
.date-native-hidden {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  pointer-events: none;
}

/* ==========================================================================
   Clean & Minimal Icon-Only Category Selection Grid
   ========================================================================== */

/* Category Sections within Panel */
.category-section {
  margin-bottom: 12px !important;
}

.category-section:last-of-type {
  margin-bottom: 0 !important;
}

.category-section-header {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  margin-bottom: 8px !important;
  gap: 8px !important;
  flex-wrap: wrap !important;
}

.category-section-title {
  font-size: 0.76rem !important;
  font-weight: 700 !important;
  color: #475569 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.04em !important;
  display: inline-flex !important;
  align-items: center !important;
  gap: 6px !important;
}

.section-grid-icon {
  width: 14px !important;
  height: 14px !important;
  color: #2563eb !important;
}

/* Compact Search Bar */
.category-search-wrapper {
  position: relative !important;
  display: inline-flex !important;
  align-items: center !important;
  max-width: 220px !important;
  width: 100% !important;
}

.category-search-wrapper .search-icon {
  position: absolute !important;
  left: 9px !important;
  width: 13px !important;
  height: 13px !important;
  color: #94a3b8 !important;
  pointer-events: none !important;
}

.category-search-input {
  width: 100% !important;
  height: 28px !important;
  padding: 2px 24px 2px 28px !important;
  font-size: 0.78rem !important;
  color: #334155 !important;
  background: #ffffff !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 14px !important;
  outline: none !important;
  transition: all 0.15s ease !important;
}

.category-search-input:focus {
  border-color: #2563eb !important;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15) !important;
}

.search-clear-btn {
  position: absolute !important;
  right: 6px !important;
  background: none !important;
  border: none !important;
  color: #94a3b8 !important;
  font-size: 0.75rem !important;
  padding: 0 !important;
  line-height: 1 !important;
  cursor: pointer !important;
}

.search-clear-btn:hover {
  color: #ef4444 !important;
}

.category-empty-search {
  padding: 16px 8px !important;
  text-align: center !important;
  font-size: 0.82rem !important;
  color: #64748b !important;
}

.category-selection-panel {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px 16px;
}

.category-icon-grid {
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: wrap !important;
  gap: 10px !important;
  align-items: center !important;
}

.category-icon-btn {
  position: relative !important;
  flex: 0 0 54px !important;
  width: 54px !important;
  height: 54px !important;
  min-width: 54px !important;
  min-height: 54px !important;
  max-width: 54px !important;
  max-height: 54px !important;
  padding: 0 !important;
  margin: 0 !important;
  background: #ffffff !important;
  border: 1.5px solid #cbd5e1 !important;
  border-radius: 12px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  cursor: pointer !important;
  color: #334155 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
  transition: all 0.15s ease-in-out !important;
  outline: none !important;
}

.category-icon-btn:hover {
  border-color: #2563eb !important;
  color: #1e40af !important;
  transform: translateY(-2px) !important;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.15) !important;
}

.category-icon-btn:focus-visible {
  border-color: #2563eb !important;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.25) !important;
}

.category-icon-btn.active {
  background: #eff6ff !important;
  border-color: #1e40af !important;
  color: #1e40af !important;
  box-shadow: 0 0 0 2px #bfdbfe, 0 3px 8px rgba(30, 64, 175, 0.18) !important;
}

.category-icon-btn .category-custom-img-icon {
  width: 2rem;
  height: 2rem;
  max-width: 32px;
  max-height: 32px;
  object-fit: contain;
  display: block;
  margin: 0 auto;
}

.category-svg-icon {
  width: 24px !important;
  height: 24px !important;
  pointer-events: none !important;
}

/* Selected Checkmark Badge */
.category-check-indicator {
  position: absolute !important;
  bottom: 2px !important;
  right: 3px !important;
  width: 14px !important;
  height: 14px !important;
  border-radius: 50% !important;
  background: #1e40af !important;
  color: #ffffff !important;
  font-size: 0.6rem !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-weight: 800 !important;
  line-height: 1 !important;
}

/* Hover & Focus Tooltip */
.category-tooltip {
  position: absolute !important;
  bottom: calc(100% + 6px) !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
  background: #0f172a !important;
  color: #ffffff !important;
  padding: 4px 10px !important;
  border-radius: 6px !important;
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  white-space: nowrap !important;
  pointer-events: none !important;
  z-index: 1000 !important;
  opacity: 0 !important;
  visibility: hidden !important;
  transition: opacity 0.12s, visibility 0.12s !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.22) !important;
}

.category-tooltip::after {
  content: "" !important;
  position: absolute !important;
  top: 100% !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
  border-width: 4px !important;
  border-style: solid !important;
  border-color: #0f172a transparent transparent transparent !important;
}

.category-icon-btn:hover .category-tooltip,
.category-icon-btn:focus-visible .category-tooltip {
  opacity: 1 !important;
  visibility: visible !important;
}

/* Selected Category Feedback Bar */
.selected-category-bar {
  margin-top: 10px !important;
  padding: 8px 12px !important;
  background: #ffffff !important;
  border: 1px solid #bfdbfe !important;
  border-radius: 8px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
}

.selected-label-text {
  font-size: 0.88rem !important;
  line-height: 1.3 !important;
}

@media (max-width: 640px) {
  .category-icon-btn {
    flex: 0 0 48px !important;
    width: 48px !important;
    height: 48px !important;
    min-width: 48px !important;
    min-height: 48px !important;
    max-width: 48px !important;
    max-height: 48px !important;
    border-radius: 10px !important;
  }
  .category-icon-btn .category-svg-icon {
    width: 20px !important;
    height: 20px !important;
  }
  .category-tooltip {
    display: none !important;
  }
  .category-selection-panel {
    padding: 10px !important;
  }
}

</style>